import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import { useAuthStore } from '../../stores/authStore';
import { useLanguageStore } from '../../stores/languageStore';
import { t } from '../../stores/languageStore';
import {
  LayoutDashboard,
  Users,
  FolderOpen,
  FileText,
  Shield,
  Settings,
  BarChart3,
  Megaphone,
  LogOut,
  ChevronRight,
  Activity,
  MessageSquare,
  Eye,
  Plus,
  Edit2,
  Trash2,
  Search,
  X,
  Save,
  RefreshCw,
  Ban,
  Unlock,
  Clock,
  Mail,
  Send,
  Key,
  Pin,
  Star,
  Package,
  Heart,
  AlertCircle,
  BookOpen,
  Menu,
  Home
} from 'lucide-react';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer
} from 'recharts';
import { api, ApiResponse, userApi } from '../../utils/api';
import { postApi } from '../../utils/api';
import { categoryApi } from '../../utils/api';
import { sensitiveWordApi } from '../../utils/api';
import { siteConfigApi } from '../../utils/api';
import { adApi } from '../../utils/api';
import { statsApi } from '../../utils/api';
import { chatMessageApi, chatBannedWordApi, chatMuteApi } from '../../utils/chatAdminApi';
import ChatAdminPanel from './ChatAdminPanel';
import AdminExpansionOrders from './AdminExpansionOrders';
import AdminDonationOrders from './AdminDonationOrders';
import AdminPendingOrders from './AdminPendingOrders';
import AdminRoomMembershipOrders from './AdminRoomMembershipOrders';
import { PREDEFINED_COLORS } from '../../utils/userGroupHelpers';

import AdminRoomManagement from './AdminRoomManagement';
import AdminExperience from './AdminExperience';
import toast from 'react-hot-toast';

interface MenuItem {
  id: string;
  label: string;
  icon: React.ElementType;
  children?: MenuItem[];
}

interface TimeSeriesData {
  time: string;
  users: number;
  posts: number;
  replies: number;
}

type RefreshPeriod = 'real-time' | '5m' | '1h' | '24h' | '7d';

const AdminMaster: React.FC = () => {
  const { user, isAuthenticated, logout, fetchUser } = useAuthStore();
  const { language } = useLanguageStore();
  const navigate = useNavigate();
  const [activeMenu, setActiveMenu] = useState('dashboard');
  const [expandedMenu, setExpandedMenu] = useState<string | null>(null);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [isMobileSidebarOpen, setIsMobileSidebarOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [loading, setLoading] = useState(true);
  const [isHydrating, setIsHydrating] = useState(true);

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (token) {
      const timer = setTimeout(() => {
        setIsHydrating(false);
      }, 500);
      return () => clearTimeout(timer);
    } else {
      setIsHydrating(false);
    }
  }, []);

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
      if (window.innerWidth < 768) {
        setSidebarCollapsed(true);
        setIsMobileSidebarOpen(false);
      } else {
        setSidebarCollapsed(false);
        setIsMobileSidebarOpen(false);
      }
    };
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  const [stats, setStats] = useState({
    totalUsers: 0,
    totalPosts: 0,
    todayPosts: 0,
    totalReplies: 0
  });

  const [timeSeriesData, setTimeSeriesData] = useState<TimeSeriesData[]>([]);
  const [refreshPeriod, setRefreshPeriod] = useState<RefreshPeriod>('1h');
  const [chartLoading, setChartLoading] = useState(false);
  const autoRefreshRef = useRef<NodeJS.Timeout | null>(null);

  const [users, setUsers] = useState<any[]>([]);
  const [userSearch, setUserSearch] = useState('');
  const [userPage, setUserPage] = useState(1);
  const [userTotal, setUserTotal] = useState(0);
  const [editingUser, setEditingUser] = useState<any>(null);
  const [userChanges, setUserChanges] = useState<Record<number, any>>({});
  const [userToDelete, setUserToDelete] = useState<number | null>(null);
  const [userGroupToDelete, setUserGroupToDelete] = useState<number | null>(null);
  const [categoryToDelete, setCategoryToDelete] = useState<number | null>(null);
  const [postToDelete, setPostToDelete] = useState<number | null>(null);
  const [sensitiveWordToDelete, setSensitiveWordToDelete] = useState<number | null>(null);
  const [adToDelete, setAdToDelete] = useState<number | null>(null);
  const [confirmAction, setConfirmAction] = useState<{
    type: 'ban' | 'unban' | 'sendVerification' | 'sendResetPassword' | 'editEmail';
    userId: number;
  } | null>(null);

  // 用户组管理
  const [userGroups, setUserGroups] = useState<any[]>([]);
  const [showUserGroupModal, setShowUserGroupModal] = useState(false);
  const [editingUserGroup, setEditingUserGroup] = useState<any>(null);
  const [userGroupForm, setUserGroupForm] = useState<{
    name: string;
    description: string;
    role: string;
    permissions: {
      post?: { delete?: boolean; approve?: boolean };
      user?: { ban?: boolean; appoint?: boolean };
      category?: { create?: boolean };
    };
    sort_order: number;
  }>({
    name: '',
    description: '',
    role: '',
    permissions: {},
    sort_order: 0
  });

  const [categories, setCategories] = useState<any[]>([]);
  const [editingCategory, setEditingCategory] = useState<any>(null);
  const [categoryForm, setCategoryForm] = useState({ name: '', description: '', parent_id: null, sort_order: 0 });
  const [showCategoryModal, setShowCategoryModal] = useState(false);

  const [posts, setPosts] = useState<any[]>([]);
  const [postPage, setPostPage] = useState(1);
  const [postTotal, setPostTotal] = useState(0);
  const [postSearch, setPostSearch] = useState('');
  const [postToMove, setPostToMove] = useState<any>(null);
  const [showMovePostModal, setShowMovePostModal] = useState(false);
  const [selectedPostCategory, setSelectedPostCategory] = useState<number | null>(null);

  const [sensitiveWords, setSensitiveWords] = useState<any[]>([]);
  const [allSensitiveWords, setAllSensitiveWords] = useState<any[]>([]);
  const [newWord, setNewWord] = useState('');
  const [searchWord, setSearchWord] = useState('');

  useEffect(() => {
    if (allSensitiveWords.length > 0) {
      filterSensitiveWords();
    }
  }, [searchWord, allSensitiveWords]);
  const [batchWords, setBatchWords] = useState('');
  const [showBatchModal, setShowBatchModal] = useState(false);

  const [configs, setConfigs] = useState<Record<string, any[]>>({});
  const [editingConfig, setEditingConfig] = useState<any>(null);
  const [editValue, setEditValue] = useState('');

  const [adSpots, setAdSpots] = useState<any[]>([]);
  const [ads, setAds] = useState<any[]>([]);
  const [selectedSpot, setSelectedSpot] = useState<number | null>(null);
  const [showAdModal, setShowAdModal] = useState(false);
  const [editingAd, setEditingAd] = useState<any>(null);
  const [adForm, setAdForm] = useState({
    title: '',
    content: '',
    image_url: '',
    link_url: '',
    ad_type: 'image' as 'image' | 'code',
    start_time: '',
    end_time: '',
    is_active: true,
    sort_order: 0
  });
  const [draggedSpotId, setDraggedSpotId] = useState<number | null>(null);
  const [draggedAdId, setDraggedAdId] = useState<number | null>(null);

  const menuItems: MenuItem[] = [
    { id: 'dashboard', label: t('admin.dashboard'), icon: LayoutDashboard },
    { id: 'users', label: t('admin.users'), icon: Users },
    { id: 'categories', label: t('admin.categories'), icon: FolderOpen },
    { id: 'posts', label: t('admin.posts'), icon: FileText },
    { id: 'sensitive-words', label: t('admin.sensitiveWordLibrary'), icon: Shield },
    { id: 'chat-admin', label: t('admin.chatManagement'), icon: MessageSquare },
    { id: 'room-management', label: t('admin.vipRoomManagement'), icon: FolderOpen },
    {
      id: 'order-management',
      label: t('admin.orderManagement'),
      icon: Package,
      children: [
        { id: 'expansion-orders', label: t('admin.expansionOrders'), icon: Package },
        { id: 'donation-orders', label: t('admin.donationOrders'), icon: Heart },
        { id: 'room-membership-orders', label: t('admin.roomMembershipOrders'), icon: Star },
        { id: 'pending-orders', label: t('admin.pendingOrders'), icon: AlertCircle },
      ]
    },
    { id: 'experience', label: t('admin.experience'), icon: BookOpen },
    { id: 'site-config', label: t('admin.siteConfig'), icon: Settings },
    { id: 'ads', label: t('admin.adManagement'), icon: Megaphone },
    { id: 'stats', label: t('admin.stats'), icon: BarChart3 },
    { id: 'operation-logs', label: t('admin.operationLogs'), icon: Activity },
  ];

  useEffect(() => {
    const init = async () => {
      if (isAuthenticated && !user) {
        await fetchUser();
      }
      setLoading(false);
    };
    init();
  }, [isAuthenticated, user, fetchUser]);

  useEffect(() => {
    if (!loading && !isHydrating) {
      if (!isAuthenticated || !['管理员', '站长', '后台设置成员'].includes(user?.role || '')) {
        navigate('/');
      }
    }
  }, [loading, isHydrating, isAuthenticated, user, navigate]);

  useEffect(() => {
    if (!loading && !isHydrating && isAuthenticated) {
      loadDashboardData();
    }
  }, [loading, isHydrating, isAuthenticated]);

  useEffect(() => {
    if (!loading && !isHydrating && isAuthenticated) {
      switch (activeMenu) {
        case 'users':
          loadUsers();
          loadUserGroups();
          break;
        case 'categories':
          loadCategories();
          break;
        case 'posts':
          loadPosts();
          break;
        case 'sensitive-words':
          loadSensitiveWords();
          break;
        case 'site-config':
          loadConfigs();
          break;
        case 'ads':
          loadAdSpots();
          break;
      }
    }
  }, [loading, activeMenu, isAuthenticated]);

  useEffect(() => {
    if (!loading && isAuthenticated && activeMenu === 'users') {
      loadUsers();
    }
  }, [userPage, loading, isAuthenticated, activeMenu]);

  useEffect(() => {
    if (!loading && isAuthenticated && activeMenu === 'posts') {
      loadPosts();
    }
  }, [postPage, postSearch, loading, isAuthenticated, activeMenu]);

  const loadDashboardData = async () => {
    try {
      const [statsRes, wordsRes, configsRes, spotsRes, categoriesRes] = await Promise.all([
        statsApi.getDashboardStats(),
        api.get<ApiResponse<any[]>>('/sensitive-words?pageSize=1000'),
        siteConfigApi.getAllConfigs(),
        adApi.getAllAdSpots(),
        categoryApi.getAllCategoriesForAdmin()
      ]);
      
      if (statsRes.success && statsRes.data) {
        setStats({
          totalUsers: statsRes.data.users,
          totalPosts: statsRes.data.posts,
          totalReplies: statsRes.data.replies,
          todayPosts: 0
        });
      }
      
      if (wordsRes.success) {
        const allWords = wordsRes.data || [];
        setAllSensitiveWords(allWords);
        setSensitiveWords(allWords);
      }
      
      if (configsRes.success) {
        setConfigs(configsRes.data || {});
      }
      
      if (spotsRes.success) {
        setAdSpots(spotsRes.data || []);
      }
      
      if (categoriesRes.success) {
        setCategories(categoriesRes.data || []);
      }
    } catch (error) {
      console.error('加载数据失败:', error);
    }
  };

  const countAllCategories = (categories: any[]): number => {
    let count = categories.length;
    for (const category of categories) {
      if (category.children && category.children.length > 0) {
        count += countAllCategories(category.children);
      }
    }
    return count;
  };

  const loadTimeSeriesData = useCallback(async () => {
    setChartLoading(true);
    try {
      const periodMap: Record<RefreshPeriod, '5m' | '1h' | '24h' | '7d'> = {
        'real-time': '5m',
        '5m': '5m',
        '1h': '1h',
        '24h': '24h',
        '7d': '7d'
      };
      const res = await statsApi.getTimeSeriesStats(periodMap[refreshPeriod]);
      if (res.success && res.data) {
        setTimeSeriesData(res.data);
      }
    } catch (error) {
      console.error('加载图表数据失败:', error);
    } finally {
      setChartLoading(false);
    }
  }, [refreshPeriod]);

  useEffect(() => {
    if (activeMenu === 'dashboard' && !loading && isAuthenticated) {
      loadTimeSeriesData();
    }
  }, [activeMenu, loading, isAuthenticated, loadTimeSeriesData]);

  useEffect(() => {
    if (autoRefreshRef.current) {
      clearInterval(autoRefreshRef.current);
    }

    if (activeMenu === 'dashboard' && !loading && isAuthenticated) {
      const intervals: Record<RefreshPeriod, number | null> = {
        'real-time': 5000,
        '5m': 5 * 60 * 1000,
        '1h': 60 * 60 * 1000,
        '24h': 24 * 60 * 60 * 1000,
        '7d': 7 * 24 * 60 * 60 * 1000
      };

      const interval = intervals[refreshPeriod];
      if (interval) {
        autoRefreshRef.current = setInterval(() => {
          loadTimeSeriesData();
        }, interval);
      }
    }

    return () => {
      if (autoRefreshRef.current) {
        clearInterval(autoRefreshRef.current);
      }
    };
  }, [activeMenu, loading, isAuthenticated, refreshPeriod, loadTimeSeriesData]);

  const loadUsers = async () => {
    try {
      const response = await userApi.getUserList({ page: userPage, pageSize: 20, search: userSearch });
      if (response.success) {
        setUsers(response.data || []);
        setUserTotal(response.pagination?.total || 0);
      }
    } catch (error) {
      toast.error('加载用户列表失败');
    }
  };

  // 用户组管理
  const loadUserGroups = async () => {
    try {
      const response = await userApi.getUserGroups();
      if (response.success) {
        setUserGroups(response.data || []);
      }
    } catch (error) {
      toast.error('加载用户组列表失败');
    }
  };

  const handleSaveUserGroup = async () => {
    try {
      if (editingUserGroup) {
        await userApi.updateUserGroup(editingUserGroup.id, userGroupForm);
        toast.success('更新用户组成功');
      } else {
        await userApi.createUserGroup({
          ...userGroupForm,
          permissions: userGroupForm.permissions || {}
        });
        toast.success('创建用户组成功');
      }
      setShowUserGroupModal(false);
      setEditingUserGroup(null);
      setUserGroupForm({ name: '', description: '', role: '', permissions: {}, sort_order: 0 });
      loadUserGroups();
    } catch (error: any) {
      const errorMessage = error.response?.data?.message || '保存用户组失败';
      toast.error(errorMessage);
    }
  };

  const handleDeleteUserGroup = (id: number) => {
    setUserGroupToDelete(id);
  };

  const confirmDeleteUserGroup = async () => {
    if (userGroupToDelete) {
      try {
        await userApi.deleteUserGroup(userGroupToDelete);
        toast.success('删除用户组成功');
        loadUserGroups();
      } catch (error) {
        toast.error('删除用户组失败');
      } finally {
        setUserGroupToDelete(null);
      }
    }
  };

  const cancelDeleteUserGroup = () => {
    setUserGroupToDelete(null);
  };

  const handleEditUserGroup = (group: any) => {
    setEditingUserGroup(group);
    setUserGroupForm({
      name: group.name,
      description: group.description || '',
      role: group.role || '',
      permissions: group.permissions || {},
      sort_order: group.sort_order || 0
    });
    setShowUserGroupModal(true);
  };

  const handleBanUser = (userId: number) => {
    setConfirmAction({ type: 'ban', userId });
  };

  const handleUnbanUser = (userId: number) => {
    setConfirmAction({ type: 'unban', userId });
  };

  const handleDeleteUser = (userId: number) => {
    setUserToDelete(userId);
  };

  const handleSendVerificationEmail = (userId: number) => {
    setConfirmAction({ type: 'sendVerification', userId });
  };

  const handleSendResetPasswordEmail = (userId: number) => {
    setConfirmAction({ type: 'sendResetPassword', userId });
  };

  const confirmActionHandler = async () => {
    if (!confirmAction) return;

    try {
      switch (confirmAction.type) {
        case 'ban':
          await userApi.banUser(confirmAction.userId);
          toast.success('用户已禁用');
          break;
        case 'unban':
          await userApi.unbanUser(confirmAction.userId);
          toast.success('用户已启用');
          break;
        case 'sendVerification':
          await userApi.sendVerificationEmail(confirmAction.userId);
          toast.success('验证邮件已发送');
          break;
        case 'sendResetPassword':
          await userApi.sendResetPasswordEmail(confirmAction.userId);
          toast.success('重置密码邮件已发送');
          break;
      }
      loadUsers();
    } catch (error: any) {
      toast.error(error.response?.data?.message || '操作失败');
    } finally {
      setConfirmAction(null);
    }
  };

  const cancelAction = () => {
    setConfirmAction(null);
  };

  const confirmDeleteUser = async () => {
    if (userToDelete) {
      try {
        await userApi.deleteUser(userToDelete);
        toast.success('用户已删除');
        loadUsers();
      } catch (error) {
        toast.error('删除用户失败');
      } finally {
        setUserToDelete(null);
      }
    }
  };

  const cancelDeleteUser = () => {
    setUserToDelete(null);
  };

  const handleSaveUserChanges = async () => {
    try {
      const userId = Object.keys(userChanges)[0];
      const changes = userChanges[parseInt(userId)];
      if (changes.email !== undefined) {
        await userApi.updateUserEmail(parseInt(userId), changes.email);
      }
      if (changes.email_verified !== undefined) {
        await userApi.setEmailVerified(parseInt(userId), changes.email_verified);
      }
      toast.success('用户信息已保存');
      setUserChanges({});
      loadUsers();
    } catch (error: any) {
      toast.error(error.response?.data?.message || '保存失败');
    }
  };

  const loadCategories = async () => {
    try {
      const response = await categoryApi.getAllCategoriesForAdmin();
      if (response.success) {
        setCategories(response.data || []);
      }
    } catch (error) {
      toast.error('加载版块列表失败');
    }
  };

  const handleSaveCategory = async () => {
    try {
      if (editingCategory) {
        await categoryApi.updateCategory(editingCategory.id, categoryForm);
        toast.success('更新版块成功');
      } else {
        await categoryApi.createCategory(categoryForm);
        toast.success('创建版块成功');
      }
      setShowCategoryModal(false);
      setEditingCategory(null);
      setCategoryForm({ name: '', description: '', parent_id: null, sort_order: 0 });
      loadCategories();
    } catch (error) {
      toast.error('保存版块失败');
    }
  };

  const handleDeleteCategory = (id: number) => {
    setCategoryToDelete(id);
  };

  const handleToggleCategoryStatus = async (id: number, currentStatus: string) => {
    const newStatus = currentStatus === 'active' ? 'hidden' : 'active';
    try {
      const response = await categoryApi.updateCategory(id, { status: newStatus });
      if (response.success) {
        toast.success(newStatus === 'active' ? '版块已显示' : '版块已隐藏');
        loadCategories();
      } else {
        toast.error(response.message || '操作失败');
      }
    } catch (error: any) {
      const errorMessage = error?.response?.data?.message || error?.message || '操作失败';
      toast.error(errorMessage);
    }
  };

  const confirmDeleteCategory = async () => {
    if (categoryToDelete) {
      try {
        const response = await categoryApi.deleteCategory(categoryToDelete);
        if (response.success) {
          toast.success('删除版块成功');
          loadCategories();
        } else {
          toast.error(response.message || '删除版块失败');
        }
      } catch (error: any) {
        const errorMessage = error?.response?.data?.message || error?.message || '删除版块失败';
        toast.error(errorMessage);
      } finally {
        setCategoryToDelete(null);
      }
    }
  };

  const cancelDeleteCategory = () => {
    setCategoryToDelete(null);
  };

  const loadPosts = async () => {
    try {
      let response;
      if (postSearch.trim()) {
        response = await postApi.searchPosts({ keyword: postSearch.trim(), page: postPage, pageSize: 20 });
      } else {
        response = await postApi.getPosts({ page: postPage, pageSize: 20 });
      }
      if (response.success) {
        setPosts(response.data || []);
        setPostTotal(response.pagination?.total || 0);
      }
    } catch (error) {
      toast.error('加载帖子列表失败');
    }
  };

  const handleDeletePost = (postId: number) => {
    setPostToDelete(postId);
  };

  const handleSetTop = async (postId: number, currentTop: string, topType: 'category' | 'global') => {
    try {
      const newTop = currentTop === topType ? 'none' : topType;
      const response = await postApi.updatePost(postId, { is_top: newTop });
      if (response.success) {
        const typeText = topType === 'global' ? '全站置顶' : '板块置顶';
        toast.success(currentTop === topType ? `取消${typeText}成功` : `${typeText}成功`);
        loadPosts();
      } else {
        toast.error(response.message || '操作失败');
      }
    } catch (error: any) {
      toast.error(error.response?.data?.message || '操作失败');
    }
  };

  const handleToggleEssence = async (postId: number, currentEssence: boolean) => {
    try {
      const response = await postApi.updatePost(postId, { is_essence: !currentEssence });
      if (response.success) {
        toast.success(currentEssence ? '取消精华成功' : '设为精华成功');
        loadPosts();
      } else {
        toast.error(response.message || '操作失败');
      }
    } catch (error: any) {
      toast.error(error.response?.data?.message || '操作失败');
    }
  };

  const confirmDeletePost = async () => {
    if (postToDelete) {
      try {
        await postApi.deletePost(postToDelete);
        toast.success('删除帖子成功');
        loadPosts();
      } catch (error) {
        toast.error('删除帖子失败');
      } finally {
        setPostToDelete(null);
      }
    }
  };

  const handleMovePost = (post: any) => {
    setPostToMove(post);
    setSelectedPostCategory(post.category_id);
    setShowMovePostModal(true);
  };

  const confirmMovePost = async () => {
    if (!postToMove || !selectedPostCategory) return;
    try {
      const response = await postApi.updatePost(postToMove.id, { category_id: selectedPostCategory });
      if (response.success) {
        toast.success('移动帖子成功');
        setShowMovePostModal(false);
        setPostToMove(null);
        loadPosts();
      }
    } catch (error: any) {
      toast.error(error.response?.data?.message || '移动失败');
    }
  };

  const cancelDeletePost = () => {
    setPostToDelete(null);
  };

  const loadSensitiveWords = async () => {
    try {
      const response = await sensitiveWordApi.getAllWords();
      if (response.success) {
        const allWords = response.data || [];
        const total = response.pagination?.total || allWords.length;
        if (total > allWords.length) {
          const pages = Math.ceil(total / 20);
          for (let page = 2; page <= pages; page++) {
            const pageResponse = await fetch(`/api/sensitive-words?page=${page}&pageSize=20`, {
              headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
            });
            const data = await pageResponse.json();
            if (data.success && data.data) {
              allWords.push(...data.data);
            }
          }
        }
        setAllSensitiveWords(allWords);
        filterSensitiveWords();
      }
    } catch (error) {
      toast.error('加载敏感词列表失败');
    }
  };

  const filterSensitiveWords = () => {
    if (!searchWord.trim()) {
      setSensitiveWords(allSensitiveWords);
    } else {
      const filtered = allSensitiveWords.filter(word => 
        word.word.toLowerCase().includes(searchWord.toLowerCase())
      );
      setSensitiveWords(filtered);
    }
  };

  const handleAddSensitiveWord = async () => {
    if (!newWord.trim()) {
      toast.error('请输入敏感词');
      return;
    }
    try {
      const response = await sensitiveWordApi.addWord(newWord.trim());
      if (response.success) {
        toast.success('添加敏感词成功');
        setNewWord('');
        setAllSensitiveWords(prev => [...prev, { id: response.data.id, word: newWord.trim(), category: 'general' }]);
        filterSensitiveWords();
      }
    } catch (error) {
      toast.error('添加敏感词失败');
    }
  };

  const handleBatchImport = async () => {
    if (!batchWords.trim()) {
      toast.error('请输入敏感词');
      return;
    }
    try {
      const words = batchWords.split('\n').filter(w => w.trim());
      for (const word of words) {
        await sensitiveWordApi.addWord(word.trim());
      }
      toast.success(`成功导入 ${words.length} 个敏感词`);
      setBatchWords('');
      setShowBatchModal(false);
      loadSensitiveWords();
    } catch (error) {
      toast.error('批量导入失败');
    }
  };

  const handleDeleteSensitiveWord = (id: number) => {
    setSensitiveWordToDelete(id);
  };

  const confirmDeleteSensitiveWord = async () => {
    if (sensitiveWordToDelete) {
      try {
        await sensitiveWordApi.deleteWord(sensitiveWordToDelete);
        toast.success('删除敏感词成功');
        setAllSensitiveWords(prev => prev.filter(w => w.id !== sensitiveWordToDelete));
        filterSensitiveWords();
      } catch (error) {
        toast.error('删除敏感词失败');
      } finally {
        setSensitiveWordToDelete(null);
      }
    }
  };

  const cancelDeleteSensitiveWord = () => {
    setSensitiveWordToDelete(null);
  };

  const loadConfigs = async () => {
    try {
      const response = await siteConfigApi.getAllConfigs();
      if (response.success) {
        setConfigs(response.data || {});
      }
    } catch (error) {
      toast.error('加载配置失败');
    }
  };

  const handleSaveConfig = async () => {
    if (!editingConfig) return;
    try {
      await siteConfigApi.updateConfig(editingConfig.config_key, editValue);
      toast.success('配置更新成功');
      setEditingConfig(null);
      loadConfigs();
    } catch (error) {
      toast.error('配置更新失败');
    }
  };

  const loadAdSpots = async () => {
    try {
      const response = await adApi.getAllAdSpots();
      if (response.success) {
        setAdSpots(response.data || []);
      }
    } catch (error) {
      toast.error('加载广告位失败');
    }
  };

  const handleDragStart = (e: React.DragEvent, spotId: number) => {
    setDraggedSpotId(spotId);
  };

  const handleDrop = async (e: React.DragEvent) => {
    e.preventDefault();
    if (!draggedSpotId) return;

    const dropTarget = e.currentTarget;
    const items = Array.from(dropTarget.children);
    let dropIndex = -1;

    for (let i = 0; i < items.length; i++) {
      const rect = items[i].getBoundingClientRect();
      const offset = e.clientY - rect.top;
      if (offset > rect.height / 2) {
        dropIndex = i + 1;
      } else if (dropIndex === -1) {
        dropIndex = i;
      }
    }

    if (dropIndex !== -1) {
      const updatedSpots = [...adSpots];
      const draggedIndex = updatedSpots.findIndex(spot => spot.id === draggedSpotId);
      if (draggedIndex !== -1) {
        const [draggedSpot] = updatedSpots.splice(draggedIndex, 1);
        updatedSpots.splice(dropIndex, 0, draggedSpot);

        // 更新排序顺序
        for (let i = 0; i < updatedSpots.length; i++) {
          try {
            await adApi.updateAdSpot(updatedSpots[i].id, { sort_order: i });
          } catch (error) {
            console.error('更新广告位排序失败:', error);
          }
        }

        // 重新加载广告位
        loadAdSpots();
        toast.success('广告位排序已更新');
      }
    }

    setDraggedSpotId(null);
  };

  const loadAdsForSpot = async (spotId: number) => {
    try {
      const response = await adApi.getAdsBySpot(spotId);
      if (response.success) {
        setAds(response.data || []);
      }
    } catch (error) {
      toast.error('加载广告列表失败');
    }
  };

  const handleAdDragStart = (e: React.DragEvent, adId: number) => {
    setDraggedAdId(adId);
  };

  const handleAdDrop = async (e: React.DragEvent) => {
    e.preventDefault();
    if (!draggedAdId || !selectedSpot) return;

    // 获取目标元素
    const dropTarget = e.target as HTMLElement;
    const adElement = dropTarget.closest('[draggable="true"]');
    
    if (!adElement) return;

    // 获取目标广告的ID
    const targetAdId = parseInt(adElement.getAttribute('data-ad-id') || '0');
    if (!targetAdId) return;

    // 直接获取当前广告列表的最新状态
    const currentAds = [...ads];
    
    // 获取目标广告和被拖动广告在数组中的索引
    const targetIndex = currentAds.findIndex(ad => ad.id === targetAdId);
    const draggedIndex = currentAds.findIndex(ad => ad.id === draggedAdId);
    
    if (targetIndex === -1 || draggedIndex === -1) {
      console.error('广告未找到');
      return;
    }

    // 创建新的广告数组
    const updatedAds = [...currentAds];
    const [draggedAd] = updatedAds.splice(draggedIndex, 1);
    updatedAds.splice(targetIndex, 0, draggedAd);

    console.log('更新前的广告顺序:', currentAds.map(ad => ({ id: ad.id, title: ad.title, sort_order: ad.sort_order })));
    console.log('更新后的广告顺序:', updatedAds.map((ad, i) => ({ id: ad.id, title: ad.title, sort_order: i })));

    // 批量更新排序顺序
    const updatePromises = updatedAds.map((ad, i) => {
      return adApi.updateAd(ad.id, { sort_order: i });
    });

    try {
      await Promise.all(updatePromises);
      console.log('排序更新成功');
      
      // 重新加载广告
      await loadAdsForSpot(selectedSpot);
      toast.success('广告排序已更新');
    } catch (error) {
      console.error('更新广告排序失败:', error);
      toast.error('更新排序失败，请重试');
    }

    setDraggedAdId(null);
  };

  const handleSaveAd = async () => {
    try {
      if (editingAd) {
        await adApi.updateAd(editingAd.id, { ...adForm, spot_id: selectedSpot! });
        toast.success('更新广告成功');
      } else {
        await adApi.addAd({ ...adForm, spot_id: selectedSpot! });
        toast.success('添加广告成功');
      }
      setShowAdModal(false);
      setEditingAd(null);
      setAdForm({
        title: '',
        content: '',
        image_url: '',
        link_url: '',
        start_time: '',
        end_time: '',
        is_active: true,
        sort_order: 0
      });
      if (selectedSpot) loadAdsForSpot(selectedSpot);
    } catch (error) {
      toast.error('保存广告失败');
    }
  };

  const handleDeleteAd = (adId: number) => {
    console.log('handleDeleteAd called with adId:', adId);
    setAdToDelete(adId);
    console.log('adToDelete state after setting:', adToDelete);
  };

  const confirmDeleteAd = async () => {
    if (adToDelete) {
      try {
        await adApi.deleteAd(adToDelete);
        toast.success('删除广告成功');
        if (selectedSpot) loadAdsForSpot(selectedSpot);
      } catch (error) {
        toast.error('删除广告失败');
      } finally {
        setAdToDelete(null);
      }
    }
  };

  const cancelDeleteAd = () => {
    setAdToDelete(null);
  };

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const renderContent = () => {
    console.log('renderContent called, activeMenu:', activeMenu);
    switch (activeMenu) {
      case 'dashboard':
        return renderDashboard();
      case 'users':
        return renderUsers();
      case 'categories':
        return renderCategories();
      case 'posts':
        return renderPosts();
      case 'sensitive-words':
        return renderSensitiveWords();
      case 'order-management':
        return <AdminPendingOrders />;
      case 'expansion-orders':
        return <AdminExpansionOrders />;
      case 'donation-orders':
        return <AdminDonationOrders />;
      case 'room-management':
        return <AdminRoomManagement />;
      case 'room-membership-orders':
        return <AdminRoomMembershipOrders />;
      case 'pending-orders':
        return <AdminPendingOrders />;
      case 'experience':
        return <AdminExperience />;
      case 'site-config':
        return renderSiteConfig();
      case 'ads':
        return renderAds();
      case 'stats':
        return renderStats();
      case 'operation-logs':
        return renderOperationLogs();
      case 'chat-admin':
        return <ChatAdminPanel />;
      default:
        return renderDashboard();
    }
  };

  const renderDashboard = () => (
    <div className="space-y-8">
      <div className="bg-gradient-to-r from-pink-500 via-rose-500 to-red-500 rounded-2xl p-8 text-white shadow-xl">
        <h2 className="text-3xl font-bold mb-2">欢迎回来，{user?.nickname}！</h2>
        <p className="text-pink-100">这是您的社区管理控制台，今天有新的活动等着您处理</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <div className="bg-white rounded-2xl p-6 shadow-sm border border-slate-100 hover:shadow-lg transition-shadow">
          <div className="flex items-center justify-between mb-4">
            <div className="w-12 h-12 rounded-xl bg-pink-100 flex items-center justify-center">
              <Users className="w-6 h-6 text-pink-600" />
            </div>
          </div>
          <h3 className="text-2xl font-bold text-slate-900">{stats.totalUsers}</h3>
          <p className="text-sm text-slate-500">总用户数</p>
        </div>

        <div className="bg-white rounded-2xl p-6 shadow-sm border border-slate-100 hover:shadow-lg transition-shadow">
          <div className="flex items-center justify-between mb-4">
            <div className="w-12 h-12 rounded-xl bg-green-100 flex items-center justify-center">
              <FileText className="w-6 h-6 text-green-600" />
            </div>
          </div>
          <h3 className="text-2xl font-bold text-slate-900">{stats.totalPosts}</h3>
          <p className="text-sm text-slate-500">总帖子数</p>
        </div>

        <div className="bg-white rounded-2xl p-6 shadow-sm border border-slate-100 hover:shadow-lg transition-shadow">
          <div className="flex items-center justify-between mb-4">
            <div className="w-12 h-12 rounded-xl bg-purple-100 flex items-center justify-center">
              <Activity className="w-6 h-6 text-purple-600" />
            </div>
          </div>
          <h3 className="text-2xl font-bold text-slate-900">{stats.todayPosts}</h3>
          <p className="text-sm text-slate-500">今日发帖</p>
        </div>

        <div className="bg-white rounded-2xl p-6 shadow-sm border border-slate-100 hover:shadow-lg transition-shadow">
          <div className="flex items-center justify-between mb-4">
            <div className="w-12 h-12 rounded-xl bg-orange-100 flex items-center justify-center">
              <MessageSquare className="w-6 h-6 text-orange-600" />
            </div>
          </div>
          <h3 className="text-2xl font-bold text-slate-900">{stats.totalReplies}</h3>
          <p className="text-sm text-slate-500">总回复数</p>
        </div>
      </div>

      <div className="bg-white rounded-2xl p-6 shadow-sm border border-slate-100">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl font-bold text-slate-900">数据趋势</h2>
          <div className="flex items-center space-x-4">
            <div className="flex items-center space-x-2 bg-slate-100 rounded-lg p-1">
              <Clock className="w-4 h-4 text-slate-500" />
              <select
                value={refreshPeriod}
                onChange={(e) => setRefreshPeriod(e.target.value as RefreshPeriod)}
                className="px-3 py-1.5 bg-transparent text-sm text-slate-700 focus:outline-none cursor-pointer"
              >
                <option value="real-time">实时刷新</option>
                <option value="5m">5分钟</option>
                <option value="1h">1小时</option>
                <option value="24h">24小时</option>
                <option value="7d">7天</option>
              </select>
            </div>
            <button
              onClick={loadTimeSeriesData}
              disabled={chartLoading}
              className="p-2 hover:bg-slate-100 rounded-lg transition-colors disabled:opacity-50"
            >
              <RefreshCw className={`w-5 h-5 text-slate-600 ${chartLoading ? 'animate-spin' : ''}`} />
            </button>
          </div>
        </div>

        <div className="h-80">
          {chartLoading && timeSeriesData.length === 0 ? (
            <div className="flex items-center justify-center h-full">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-pink-600"></div>
            </div>
          ) : (
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={timeSeriesData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                <XAxis
                  dataKey="time"
                  stroke="#64748b"
                  fontSize={12}
                  tickFormatter={(value) => {
                    const date = new Date(value);
                    if (refreshPeriod === 'real-time' || refreshPeriod === '5m') {
                      return date.toLocaleTimeString('zh-CN', { hour: '2-digit', minute: '2-digit' });
                    } else if (refreshPeriod === '1h') {
                      return date.toLocaleTimeString('zh-CN', { hour: '2-digit', minute: '2-digit' });
                    } else {
                      return date.toLocaleDateString('zh-CN', { month: 'short', day: 'numeric' });
                    }
                  }}
                />
                <YAxis stroke="#64748b" fontSize={12} />
                <Tooltip
                  contentStyle={{
                    backgroundColor: '#fff',
                    border: '1px solid #e2e8f0',
                    borderRadius: '8px',
                    fontSize: '12px'
                  }}
                  labelFormatter={(value) => new Date(value).toLocaleString('zh-CN')}
                />
                <Legend />
                <Line
                  type="monotone"
                  dataKey="users"
                  stroke="#3b82f6"
                  strokeWidth={2}
                  dot={{ fill: '#3b82f6', strokeWidth: 2 }}
                  name="用户"
                  activeDot={{ r: 6 }}
                />
                <Line
                  type="monotone"
                  dataKey="posts"
                  stroke="#22c55e"
                  strokeWidth={2}
                  dot={{ fill: '#22c55e', strokeWidth: 2 }}
                  name="帖子"
                  activeDot={{ r: 6 }}
                />
                <Line
                  type="monotone"
                  dataKey="replies"
                  stroke="#f59e0b"
                  strokeWidth={2}
                  dot={{ fill: '#f59e0b', strokeWidth: 2 }}
                  name="回复"
                  activeDot={{ r: 6 }}
                />
              </LineChart>
            </ResponsiveContainer>
          )}
        </div>

        <div className="mt-4 flex items-center justify-end text-sm text-slate-500">
          {refreshPeriod === 'real-time' && <span className="flex items-center"><span className="w-2 h-2 bg-green-500 rounded-full mr-2 animate-pulse"></span>每5秒自动刷新</span>}
          {refreshPeriod === '5m' && <span>每5分钟自动刷新</span>}
          {refreshPeriod === '1h' && <span>每小时自动刷新</span>}
          {refreshPeriod === '24h' && <span>每天自动刷新</span>}
          {refreshPeriod === '7d' && <span>每7天自动刷新</span>}
        </div>
      </div>
    </div>
  );

  const renderUsers = () => (
    <div className="space-y-6">
      <div className="bg-white rounded-2xl p-6 shadow-sm border border-slate-100">
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-4 sm:mb-6">
          <h2 className="text-lg sm:text-xl font-bold text-slate-900">用户管理</h2>
          <div className="flex flex-wrap items-center gap-2 sm:gap-4 w-full sm:w-auto">
            <div className="relative flex-1 sm:flex-none">
              <input
                type="text"
                placeholder="搜索用户..."
                value={userSearch}
                onChange={(e) => setUserSearch(e.target.value)}
                onKeyPress={(e) => {
                  if (e.key === 'Enter') {
                    loadUsers();
                  }
                }}
                className="w-full sm:w-64 pl-10 pr-4 py-2 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-pink-500 text-sm"
              />
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-400" />
            </div>
            <button onClick={loadUsers} className="px-3 sm:px-4 py-2 bg-pink-600 text-white rounded-xl hover:bg-pink-700 text-sm whitespace-nowrap">
              搜索
            </button>
            <button onClick={() => { setUserSearch(''); setUserPage(1); loadUsers(); }} className="px-3 sm:px-4 py-2 border border-slate-200 rounded-xl hover:bg-slate-100 text-sm whitespace-nowrap">
              重置
            </button>
            <button onClick={loadUsers} className="p-2 hover:bg-slate-100 rounded-xl transition-colors" title="刷新">
              <RefreshCw className="w-4 h-4 text-slate-600" />
            </button>
            {Object.keys(userChanges).length > 0 && (
              <button
                onClick={handleSaveUserChanges}
                className="flex items-center space-x-1 sm:space-x-2 px-3 sm:px-4 py-2 bg-green-600 text-white rounded-xl hover:bg-green-700 text-sm whitespace-nowrap"
              >
                <Save className="w-4 h-4" />
                <span>保存修改</span>
              </button>
            )}
          </div>
        </div>

        {/* 删除确认对话框 */}
        {userToDelete && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-2xl p-6 w-96 shadow-lg">
              <h3 className="text-lg font-semibold text-slate-900 mb-4">确认删除</h3>
              <p className="text-slate-600 mb-6">确定要删除该用户吗？此操作不可恢复！</p>
              <div className="flex items-center justify-end space-x-4">
                <button
                  onClick={cancelDeleteUser}
                  className="px-4 py-2 border border-slate-200 rounded-xl hover:bg-slate-100"
                >
                  取消
                </button>
                <button
                  onClick={confirmDeleteUser}
                  className="px-4 py-2 bg-red-600 text-white rounded-xl hover:bg-red-700"
                >
                  确认删除
                </button>
              </div>
            </div>
          </div>
        )}

        {/* 用户组删除确认对话框 */}
        {userGroupToDelete && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-2xl p-6 w-96 shadow-lg">
              <h3 className="text-lg font-semibold text-slate-900 mb-4">确认删除</h3>
              <p className="text-slate-600 mb-6">确定要删除这个用户组吗？此操作不可恢复！</p>
              <div className="flex items-center justify-end space-x-4">
                <button
                  onClick={cancelDeleteUserGroup}
                  className="px-4 py-2 border border-slate-200 rounded-xl hover:bg-slate-100"
                >
                  取消
                </button>
                <button
                  onClick={confirmDeleteUserGroup}
                  className="px-4 py-2 bg-red-600 text-white rounded-xl hover:bg-red-700"
                >
                  确认删除
                </button>
              </div>
            </div>
          </div>
        )}

        {/* 版块删除确认对话框 */}
        {categoryToDelete && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-2xl p-6 w-96 shadow-lg">
              <h3 className="text-lg font-semibold text-slate-900 mb-4">确认删除</h3>
              <p className="text-slate-600 mb-6">确定要删除这个版块吗？此操作不可恢复！</p>
              <div className="flex items-center justify-end space-x-4">
                <button
                  onClick={cancelDeleteCategory}
                  className="px-4 py-2 border border-slate-200 rounded-xl hover:bg-slate-100"
                >
                  取消
                </button>
                <button
                  onClick={confirmDeleteCategory}
                  className="px-4 py-2 bg-red-600 text-white rounded-xl hover:bg-red-700"
                >
                  确认删除
                </button>
              </div>
            </div>
          </div>
        )}

        {/* 帖子删除确认对话框 */}
        {postToDelete && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-2xl p-6 w-96 shadow-lg">
              <h3 className="text-lg font-semibold text-slate-900 mb-4">确认删除</h3>
              <p className="text-slate-600 mb-6">确定要删除这篇帖子吗？此操作不可恢复！</p>
              <div className="flex items-center justify-end space-x-4">
                <button
                  onClick={cancelDeletePost}
                  className="px-4 py-2 border border-slate-200 rounded-xl hover:bg-slate-100"
                >
                  取消
                </button>
                <button
                  onClick={confirmDeletePost}
                  className="px-4 py-2 bg-red-600 text-white rounded-xl hover:bg-red-700"
                >
                  确认删除
                </button>
              </div>
            </div>
          </div>
        )}

        {/* 敏感词删除确认对话框 */}
        {sensitiveWordToDelete && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-2xl p-6 w-96 shadow-lg">
              <h3 className="text-lg font-semibold text-slate-900 mb-4">确认删除</h3>
              <p className="text-slate-600 mb-6">确定要删除该敏感词吗？此操作不可恢复！</p>
              <div className="flex items-center justify-end space-x-4">
                <button
                  onClick={cancelDeleteSensitiveWord}
                  className="px-4 py-2 border border-slate-200 rounded-xl hover:bg-slate-100"
                >
                  取消
                </button>
                <button
                  onClick={confirmDeleteSensitiveWord}
                  className="px-4 py-2 bg-red-600 text-white rounded-xl hover:bg-red-700"
                >
                  确认删除
                </button>
              </div>
            </div>
          </div>
        )}

        {/* 广告删除确认对话框 */}
        {adToDelete && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-2xl p-6 w-96 shadow-lg">
              <h3 className="text-lg font-semibold text-slate-900 mb-4">确认删除</h3>
              <p className="text-slate-600 mb-6">确定要删除这个广告吗？此操作不可恢复！</p>
              <div className="flex items-center justify-end space-x-4">
                <button
                  onClick={cancelDeleteAd}
                  className="px-4 py-2 border border-slate-200 rounded-xl hover:bg-slate-100"
                >
                  取消
                </button>
                <button
                  onClick={confirmDeleteAd}
                  className="px-4 py-2 bg-red-600 text-white rounded-xl hover:bg-red-700"
                >
                  确认删除
                </button>
              </div>
            </div>
          </div>
        )}

        {/* 操作确认对话框 */}
        {confirmAction && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-2xl p-6 w-96 shadow-lg">
              <h3 className="text-lg font-semibold text-slate-900 mb-4">确认操作</h3>
              <p className="text-slate-600 mb-6">
                {confirmAction.type === 'ban' && '确定要禁用该用户吗？'}
                {confirmAction.type === 'unban' && '确定要启用该用户吗？'}
                {confirmAction.type === 'sendVerification' && '确定要发送验证邮件吗？'}
                {confirmAction.type === 'sendResetPassword' && '确定要发送重置密码邮件吗？'}
              </p>
              <div className="flex items-center justify-end space-x-4">
                <button
                  onClick={cancelAction}
                  className="px-4 py-2 border border-slate-200 rounded-xl hover:bg-slate-100"
                >
                  取消
                </button>
                <button
                  onClick={confirmActionHandler}
                  className="px-4 py-2 bg-pink-600 text-white rounded-xl hover:bg-pink-700"
                >
                  确认
                </button>
              </div>
            </div>
          </div>
        )}

        <div className="overflow-x-auto -mx-4 sm:-mx-6 lg:-mx-8 px-4 sm:px-6 lg:px-8">
          <table className="w-full min-w-[640px]">
            <thead>
              <tr className="border-b border-slate-200">
                <th className="text-left py-2 sm:py-3 px-3 sm:px-4 font-medium text-slate-600 text-xs sm:text-sm">ID</th>
                <th className="text-left py-2 sm:py-3 px-3 sm:px-4 font-medium text-slate-600 text-xs sm:text-sm">用户</th>
                <th className="text-left py-2 sm:py-3 px-3 sm:px-4 font-medium text-slate-600 text-xs sm:text-sm hidden sm:table-cell">邮箱</th>
                <th className="text-left py-2 sm:py-3 px-3 sm:px-4 font-medium text-slate-600 text-xs sm:text-sm">用户组</th>
                <th className="text-left py-2 sm:py-3 px-3 sm:px-4 font-medium text-slate-600 text-xs sm:text-sm">状态</th>
                <th className="text-left py-2 sm:py-3 px-3 sm:px-4 font-medium text-slate-600 text-xs sm:text-sm hidden md:table-cell">邮箱验证</th>
                <th className="text-left py-2 sm:py-3 px-3 sm:px-4 font-medium text-slate-600 text-xs sm:text-sm hidden md:table-cell">注册时间</th>
                <th className="text-left py-2 sm:py-3 px-3 sm:px-4 font-medium text-slate-600 text-xs sm:text-sm">操作</th>
              </tr>
            </thead>
            <tbody>
              {users.map((u) => (
                <tr key={u.id} className="border-b border-slate-100 hover:bg-slate-50">
                  <td className="py-2 sm:py-3 px-3 sm:px-4">
                    <span className="text-xs sm:text-sm text-slate-500">{u.id}</span>
                  </td>
                  <td className="py-2 sm:py-3 px-3 sm:px-4">
                    <div className="flex items-center space-x-2 sm:space-x-3">
                      <img
                        src={u.avatar || `https://ui-avatars.com/api/?name=${u.nickname || u.username || 'User'}&background=3b82f6&color=fff`}
                        alt={u.nickname || u.username}
                        className="w-8 h-8 sm:w-10 sm:h-10 rounded-full"
                      />
                      <div>
                        <p className="font-medium text-slate-900 text-xs sm:text-sm">
                          {u.nickname}
                          {u.user_group_name && (
                            <span className="ml-1 sm:ml-2 px-1.5 sm:px-2 py-0.5 bg-pink-100 text-pink-700 text-[10px] sm:text-xs rounded-full">
                              {u.user_group_name}
                            </span>
                          )}
                        </p>
                        <p className="text-[10px] sm:text-xs text-slate-500">@{u.username}</p>
                      </div>
                    </div>
                  </td>
                  <td className="py-2 sm:py-3 px-3 sm:px-4 hidden sm:table-cell">
                    {editingUser === u.id ? (
                      <input
                        type="email"
                        defaultValue={u.email}
                        onChange={(e) => {
                          const newEmail = e.target.value;
                          setUserChanges(prev => ({
                            ...prev,
                            [u.id]: { ...prev[u.id], email: newEmail }
                          }));
                        }}
                        className="px-2 py-1 border border-pink-400 rounded text-xs sm:text-sm w-full sm:w-48"
                        autoFocus
                        onBlur={() => setEditingUser(null)}
                      />
                    ) : (
                      <span className="text-slate-600 text-xs sm:text-sm">{u.email}</span>
                    )}
                  </td>
                  <td className="py-2 sm:py-3 px-3 sm:px-4">
                    <select
                      value={userChanges[u.id]?.user_group_id ?? u.user_group_id ?? ''}
                      onChange={async (e) => {
                        const userGroupId = e.target.value === '' ? null : parseInt(e.target.value);
                        try {
                          await userApi.setUserGroup(u.id, userGroupId);
                          
                          if (userGroupId) {
                            const group = userGroups.find(g => g.id === userGroupId);
                            if (group && group.role) {
                              await userApi.updateUserRole(u.id, group.role);
                            }
                          }
                          
                          toast.success('用户组更新成功');
                          loadUsers();
                        } catch (error) {
                          toast.error('用户组更新失败');
                        }
                      }}
                      className="px-2 py-1 border border-slate-200 rounded text-sm"
                    >
                      <option value="">无用户组</option>
                      {userGroups.map((group) => (
                        <option key={group.id} value={group.id}>
                          {group.name}
                        </option>
                      ))}
                    </select>
                  </td>
                  <td className="py-2 sm:py-3 px-3 sm:px-4">
                    <span className={`px-1.5 sm:px-2 py-0.5 sm:py-1 rounded-full text-[10px] sm:text-xs font-medium ${
                      u.status === 'active' ? 'bg-green-100 text-green-700' :
                      'bg-red-100 text-red-700'
                    }`}>
                      {u.status === 'active' ? '正常' : '禁用'}
                    </span>
                  </td>
                  <td className="py-2 sm:py-3 px-3 sm:px-4 hidden md:table-cell">
                    <button
                      onClick={() => {
                        const newVerified = u.email_verified ? false : true;
                        setUserChanges(prev => ({
                          ...prev,
                          [u.id]: { ...prev[u.id], email_verified: newVerified }
                        }));
                      }}
                      className={`px-1.5 sm:px-2 py-0.5 sm:py-1 rounded-full text-[10px] sm:text-xs font-medium cursor-pointer ${
                        u.email_verified ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
                      }`}
                    >
                      {u.email_verified ? '已验证' : '未验证'}
                    </button>
                  </td>
                  <td className="py-2 sm:py-3 px-3 sm:px-4 text-slate-500 text-[10px] sm:text-sm hidden md:table-cell">
                    {new Date(u.created_at).toLocaleDateString()}
                  </td>
                  <td className="py-2 sm:py-3 px-3 sm:px-4">
                    <div className="flex items-center space-x-1 sm:space-x-2">
                      {u.role !== '站长' && (
                        <>
                          <button
                            onClick={() => handleDeleteUser(u.id)}
                            className="p-1 sm:p-1.5 text-red-600 hover:text-red-800"
                            title="删除用户"
                          >
                            <Trash2 className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                          </button>
                          {u.status === 'active' ? (
                            <button
                              onClick={() => handleBanUser(u.id)}
                              className="p-1 sm:p-1.5 text-red-600 hover:text-red-800"
                              title="禁用用户"
                            >
                              <Ban className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                            </button>
                          ) : (
                            <button
                              onClick={() => handleUnbanUser(u.id)}
                              className="p-1 sm:p-1.5 text-green-600 hover:text-green-800"
                              title="启用用户"
                            >
                              <Unlock className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                            </button>
                          )}
                          <button
                            onClick={() => setEditingUser(u.id)}
                            className="p-1 sm:p-1.5 text-pink-600 hover:text-pink-800"
                            title="修改邮箱"
                          >
                            <Mail className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                          </button>
                          <button
                            onClick={() => handleSendVerificationEmail(u.id)}
                            className="p-1 sm:p-1.5 text-purple-600 hover:text-purple-800"
                            title="发送验证邮件"
                          >
                            <Send className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                          </button>
                          <button
                            onClick={() => handleSendResetPasswordEmail(u.id)}
                            className="p-1 sm:p-1.5 text-orange-600 hover:text-orange-800"
                            title="发送重置密码邮件"
                          >
                            <Key className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                          </button>
                        </>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="mt-4 flex items-center justify-between">
          <p className="text-sm text-slate-500">共 {userTotal} 个用户</p>
          <div className="flex items-center space-x-2">
            <button onClick={() => setUserPage(p => Math.max(1, p - 1))} disabled={userPage === 1}
              className="px-3 py-1 border border-slate-200 rounded-lg disabled:opacity-50">
              上一页
            </button>
            <span className="text-sm text-slate-600">第 {userPage} 页</span>
            <button onClick={() => setUserPage(p => p + 1)} disabled={users.length < 20}
              className="px-3 py-1 border border-slate-200 rounded-lg disabled:opacity-50">
              下一页
            </button>
          </div>
        </div>
      </div>

      {/* 用户组管理 */}
      <div className="bg-white rounded-2xl p-6 shadow-sm border border-slate-100">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl font-bold text-slate-900">用户组管理</h2>
          <button
            onClick={() => {
              setEditingUserGroup(null);
              setUserGroupForm({ name: '', description: '', role: '', permissions: {}, sort_order: 0 });
              setShowUserGroupModal(true);
            }}
            className="flex items-center space-x-2 px-4 py-2 bg-pink-600 text-white rounded-xl hover:bg-pink-700"
          >
            <Plus className="w-4 h-4" />
            <span>创建用户组</span>
          </button>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-slate-200">
                <th className="text-left py-3 px-4 font-medium text-slate-600">用户组名称</th>
                <th className="text-left py-3 px-4 font-medium text-slate-600">描述</th>
                <th className="text-left py-3 px-4 font-medium text-slate-600">排序</th>
                <th className="text-left py-3 px-4 font-medium text-slate-600">创建时间</th>
                <th className="text-left py-3 px-4 font-medium text-slate-600">操作</th>
              </tr>
            </thead>
            <tbody>
              {userGroups.map((group) => (
                <tr key={group.id} className="border-b border-slate-100 hover:bg-slate-50">
                  <td className="py-3 px-4 font-medium text-slate-900">{group.name}</td>
                  <td className="py-3 px-4 text-slate-600">{group.description || '-'}</td>
                  <td className="py-3 px-4 text-slate-600">{group.sort_order}</td>
                  <td className="py-3 px-4 text-slate-500 text-sm">
                    {new Date(group.created_at).toLocaleDateString()}
                  </td>
                  <td className="py-3 px-4">
                    <div className="flex items-center space-x-2">
                      <button
                        onClick={() => handleEditUserGroup(group)}
                        className="p-1 text-pink-600 hover:text-pink-800"
                        title="编辑用户组"
                      >
                        <Edit2 className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => handleDeleteUserGroup(group.id)}
                        className="p-1 text-red-600 hover:text-red-800"
                        title="删除用户组"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {userGroups.length === 0 && (
          <div className="py-12 text-center">
            <p className="text-slate-500">暂无用户组</p>
            <button
              onClick={() => {
                setEditingUserGroup(null);
                setUserGroupForm({ name: '', description: '', role: '', permissions: {}, sort_order: 0 });
                setShowUserGroupModal(true);
              }}
              className="mt-4 px-4 py-2 bg-pink-600 text-white rounded-xl hover:bg-pink-700"
            >
              创建第一个用户组
            </button>
          </div>
        )}
      </div>

      {/* 用户组编辑模态框 */}
      {showUserGroupModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl p-6 w-full max-w-md shadow-lg max-h-[85vh] flex flex-col">
            <h3 className="text-lg font-semibold text-slate-900 mb-4">
              {editingUserGroup ? '编辑用户组' : '创建用户组'}
            </h3>
            <div className="space-y-4 overflow-y-auto flex-1">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">用户组名称</label>
                <input
                  type="text"
                  value={userGroupForm.name}
                  onChange={(e) => setUserGroupForm({ ...userGroupForm, name: e.target.value })}
                  className="w-full px-3 py-2 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-pink-500"
                  placeholder="请输入用户组名称"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">描述</label>
                <textarea
                  value={userGroupForm.description}
                  onChange={(e) => setUserGroupForm({ ...userGroupForm, description: e.target.value })}
                  className="w-full px-3 py-2 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-pink-500"
                  placeholder="请输入用户组描述"
                  rows={3}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">排序</label>
                <input
                  type="number"
                  value={userGroupForm.sort_order}
                  onChange={(e) => setUserGroupForm({ ...userGroupForm, sort_order: parseInt(e.target.value) || 0 })}
                  className="w-full px-3 py-2 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-pink-500"
                  placeholder="请输入排序号"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">对应角色</label>
                <select
                  value={userGroupForm.role || ''}
                  onChange={(e) => setUserGroupForm({ ...userGroupForm, role: e.target.value })}
                  className="w-full px-3 py-2 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-pink-500"
                >
                  <option value="">无对应角色</option>
                  {userGroups
                    .filter(group => editingUserGroup ? group.id !== editingUserGroup.id : true)
                    .sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0))
                    .map((group) => (
                      <option key={group.id} value={group.name}>
                        {group.name}
                      </option>
                    ))
                  }
                </select>
                <p className="text-xs text-slate-500 mt-1">
                  选择其他用户组作为对应的角色分类，或者留空表示无对应角色
                </p>
              </div>
              {/* 颜色设置 */}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">角色徽章颜色</label>
                <div className="flex items-center space-x-3">
                  <input
                    type="color"
                    value={userGroupForm.badge_color || '#6B7280'}
                    onChange={(e) => setUserGroupForm({ ...userGroupForm, badge_color: e.target.value })}
                    className="w-10 h-10 rounded border border-slate-300 cursor-pointer"
                  />
                  <input
                    type="text"
                    value={userGroupForm.badge_color || '#6B7280'}
                    onChange={(e) => setUserGroupForm({ ...userGroupForm, badge_color: e.target.value })}
                    className="flex-1 px-3 py-2 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-pink-500"
                    placeholder="#6B7280"
                  />
                  <div className="flex gap-1">
                    {PREDEFINED_COLORS.slice(0, 8).map(color => (
                      <button
                        key={color.value}
                        type="button"
                        onClick={() => setUserGroupForm({ ...userGroupForm, badge_color: color.value })}
                        className="w-6 h-6 rounded-full border-2 border-white shadow-sm hover:scale-110 transition-transform"
                        style={{ backgroundColor: color.value }}
                        title={color.name}
                      />
                    ))}
                  </div>
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">昵称颜色</label>
                <div className="flex items-center space-x-3">
                  <input
                    type="color"
                    value={userGroupForm.nickname_color || '#111827'}
                    onChange={(e) => setUserGroupForm({ ...userGroupForm, nickname_color: e.target.value })}
                    className="w-10 h-10 rounded border border-slate-300 cursor-pointer"
                  />
                  <input
                    type="text"
                    value={userGroupForm.nickname_color || '#111827'}
                    onChange={(e) => setUserGroupForm({ ...userGroupForm, nickname_color: e.target.value })}
                    className="flex-1 px-3 py-2 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-pink-500"
                    placeholder="#111827"
                  />
                  <div className="flex gap-1">
                    {PREDEFINED_COLORS.slice(0, 8).map(color => (
                      <button
                        key={color.value}
                        type="button"
                        onClick={() => setUserGroupForm({ ...userGroupForm, nickname_color: color.value })}
                        className="w-6 h-6 rounded-full border-2 border-white shadow-sm hover:scale-110 transition-transform"
                        style={{ backgroundColor: color.value }}
                        title={color.name}
                      />
                    ))}
                  </div>
                </div>
              </div>
              {/* 预览效果 */}
              {(userGroupForm.badge_color || userGroupForm.nickname_color) && (
                <div className="bg-slate-50 p-3 rounded-xl">
                  <label className="block text-sm font-medium text-slate-700 mb-2">效果预览</label>
                  <div className="flex items-center space-x-2">
                    <div className="w-8 h-8 rounded-full bg-pink-500"></div>
                    <div className="flex items-center">
                      {userGroupForm.role && (
                        <span
                          className="text-xs px-1.5 py-0.5 rounded mr-2"
                          style={{
                            backgroundColor: userGroupForm.badge_color + '20',
                            color: userGroupForm.badge_color,
                            border: `1px solid ${userGroupForm.badge_color}40`
                          }}
                        >
                          {userGroupForm.role}
                        </span>
                      )}
                      <span className="text-sm font-medium" style={{ color: userGroupForm.nickname_color }}>
                        用户昵称
                      </span>
                    </div>
                  </div>
                </div>
              )}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">权限设置</label>
                <div className="space-y-3 mt-2">
                  <div className="flex items-center space-x-2">
                    <input
                      type="checkbox"
                      checked={userGroupForm.permissions?.post?.delete === true}
                      onChange={(e) => setUserGroupForm({
                        ...userGroupForm,
                        permissions: {
                          ...userGroupForm.permissions,
                          post: {
                            ...userGroupForm.permissions?.post,
                            delete: e.target.checked
                          }
                        }
                      })}
                      className="w-4 h-4 text-pink-600 rounded"
                    />
                    <span className="text-sm text-slate-700">删帖</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <input
                      type="checkbox"
                      checked={userGroupForm.permissions?.post?.approve === true}
                      onChange={(e) => setUserGroupForm({
                        ...userGroupForm,
                        permissions: {
                          ...userGroupForm.permissions,
                          post: {
                            ...userGroupForm.permissions?.post,
                            approve: e.target.checked
                          }
                        }
                      })}
                      className="w-4 h-4 text-pink-600 rounded"
                    />
                    <span className="text-sm text-slate-700">审核帖子</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <input
                      type="checkbox"
                      checked={userGroupForm.permissions?.user?.ban === true}
                      onChange={(e) => setUserGroupForm({
                        ...userGroupForm,
                        permissions: {
                          ...userGroupForm.permissions,
                          user: {
                            ...userGroupForm.permissions?.user,
                            ban: e.target.checked
                          }
                        }
                      })}
                      className="w-4 h-4 text-pink-600 rounded"
                    />
                    <span className="text-sm text-slate-700">禁用账户</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <input
                      type="checkbox"
                      checked={userGroupForm.permissions?.category?.create === true}
                      onChange={(e) => setUserGroupForm({
                        ...userGroupForm,
                        permissions: {
                          ...userGroupForm.permissions,
                          category: {
                            ...userGroupForm.permissions?.category,
                            create: e.target.checked
                          }
                        }
                      })}
                      className="w-4 h-4 text-pink-600 rounded"
                    />
                    <span className="text-sm text-slate-700">创建频道</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <input
                      type="checkbox"
                      checked={userGroupForm.permissions?.user?.appoint === true}
                      onChange={(e) => setUserGroupForm({
                        ...userGroupForm,
                        permissions: {
                          ...userGroupForm.permissions,
                          user: {
                            ...userGroupForm.permissions?.user,
                            appoint: e.target.checked
                          }
                        }
                      })}
                      className="w-4 h-4 text-pink-600 rounded"
                    />
                    <span className="text-sm text-slate-700">任命用户角色</span>
                  </div>
                </div>
              </div>
            </div>
            <div className="mt-6 flex items-center justify-end space-x-4 border-t border-slate-100 pt-4">
              <button
                onClick={() => setShowUserGroupModal(false)}
                className="px-4 py-2 border border-slate-200 rounded-xl hover:bg-slate-100"
              >
                取消
              </button>
              <button
                onClick={handleSaveUserGroup}
                className="px-4 py-2 bg-pink-600 text-white rounded-xl hover:bg-pink-700"
              >
                保存
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );

  const renderCategories = () => (
    <div className="space-y-6">
      <div className="bg-white rounded-2xl p-6 shadow-sm border border-slate-100">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl font-bold text-slate-900">版块管理</h2>
          <button
            onClick={() => {
              setEditingCategory(null);
              setCategoryForm({ name: '', description: '', parent_id: null, sort_order: 0 });
              setShowCategoryModal(true);
            }}
            className="flex items-center space-x-2 px-4 py-2 bg-pink-600 text-white rounded-xl hover:bg-pink-700"
          >
            <Plus className="w-4 h-4" />
            <span>添加版块</span>
          </button>
        </div>

        <div className="space-y-3">
          {categories.map((parentCategory) => (
            <div key={parentCategory.id} className="border border-slate-200 rounded-xl overflow-hidden">
              <div className="bg-slate-50 px-4 py-3 border-b border-slate-200">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <h3 className="font-semibold text-slate-900 flex items-center">
                      <span className="w-2 h-2 bg-pink-500 rounded-full mr-2"></span>
                      {parentCategory.name}
                    </h3>
                    <p className="text-sm text-slate-500 mt-1">{parentCategory.description || '暂无描述'}</p>
                    <div className="flex items-center space-x-4 mt-2 text-xs text-slate-400">
                      <span>排序: {parentCategory.sort_order || 0}</span>
                      <span>帖子: {parentCategory.posts_count || 0}</span>
                    </div>
                  </div>
                  <div className="flex items-center space-x-2">
                      <span className={`px-2 py-1 text-xs rounded-full ${parentCategory.status === 'active' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                        {parentCategory.status === 'active' ? '显示' : '隐藏'}
                      </span>
                      <button onClick={() => handleToggleCategoryStatus(parentCategory.id, parentCategory.status)} className="p-2 hover:bg-slate-200 rounded-lg">
                        <Eye className="w-4 h-4 text-slate-600" />
                      </button>
                      <button onClick={() => {
                        setEditingCategory(parentCategory);
                        setCategoryForm({
                          name: parentCategory.name,
                          description: parentCategory.description || '',
                          parent_id: parentCategory.parent_id || null,
                          sort_order: parentCategory.sort_order || 0
                        });
                        setShowCategoryModal(true);
                      }} className="p-2 hover:bg-slate-200 rounded-lg">
                        <Edit2 className="w-4 h-4 text-slate-600" />
                      </button>
                      <button onClick={() => handleDeleteCategory(parentCategory.id)} className="p-2 hover:bg-red-50 rounded-lg">
                        <Trash2 className="w-4 h-4 text-red-600" />
                      </button>
                    </div>
                </div>
              </div>
              {parentCategory.children && parentCategory.children.length > 0 && (
                <div className="bg-white">
                  {parentCategory.children.sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0)).map((childCategory) => (
                    <div key={childCategory.id} className="px-4 py-3 border-b border-slate-100 last:border-b-0 ml-4">
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <h4 className="font-medium text-slate-800 flex items-center">
                            <span className="w-1.5 h-1.5 bg-slate-400 rounded-full mr-2"></span>
                            {childCategory.name}
                          </h4>
                          <p className="text-sm text-slate-500 mt-1">{childCategory.description || '暂无描述'}</p>
                          <div className="flex items-center space-x-4 mt-2 text-xs text-slate-400">
                            <span>排序: {childCategory.sort_order || 0}</span>
                            <span>帖子: {childCategory.posts_count || 0}</span>
                          </div>
                        </div>
                        <div className="flex items-center space-x-2">
                          <span className={`px-2 py-1 text-xs rounded-full ${childCategory.status === 'active' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                            {childCategory.status === 'active' ? '显示' : '隐藏'}
                          </span>
                          <button onClick={() => handleToggleCategoryStatus(childCategory.id, childCategory.status)} className="p-2 hover:bg-slate-100 rounded-lg">
                            <Eye className="w-4 h-4 text-slate-600" />
                          </button>
                          <button onClick={() => {
                            setEditingCategory(childCategory);
                            setCategoryForm({
                              name: childCategory.name,
                              description: childCategory.description || '',
                              parent_id: childCategory.parent_id || null,
                              sort_order: childCategory.sort_order || 0
                            });
                            setShowCategoryModal(true);
                          }} className="p-2 hover:bg-slate-100 rounded-lg">
                            <Edit2 className="w-4 h-4 text-slate-600" />
                          </button>
                          <button onClick={() => handleDeleteCategory(childCategory.id)} className="p-2 hover:bg-red-50 rounded-lg">
                            <Trash2 className="w-4 h-4 text-red-600" />
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      </div>

      {showCategoryModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-2xl p-6 w-full max-w-md">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-bold text-slate-900">
                {editingCategory ? '编辑版块' : '添加版块'}
              </h3>
              <button onClick={() => setShowCategoryModal(false)}>
                <X className="w-5 h-5 text-slate-500" />
              </button>
            </div>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">版块名称</label>
                <input
                  type="text"
                  value={categoryForm.name}
                  onChange={(e) => setCategoryForm({ ...categoryForm, name: e.target.value })}
                  className="w-full px-3 py-2 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-pink-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">父版块（可选）</label>
                <select
                  value={categoryForm.parent_id || ''}
                  onChange={(e) => setCategoryForm({ ...categoryForm, parent_id: e.target.value ? parseInt(e.target.value) : null })}
                  className="w-full px-3 py-2 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-pink-500"
                >
                  <option value="">无（作为顶级版块）</option>
                  {categories
                    .filter(c => c.id !== editingCategory?.id)
                    .map((category) => (
                      <option key={category.id} value={category.id}>
                        {category.name}
                      </option>
                    ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">版块描述</label>
                <textarea
                  value={categoryForm.description}
                  onChange={(e) => setCategoryForm({ ...categoryForm, description: e.target.value })}
                  rows={3}
                  className="w-full px-3 py-2 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-pink-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">排序</label>
                <input
                  type="number"
                  value={categoryForm.sort_order}
                  onChange={(e) => setCategoryForm({ ...categoryForm, sort_order: parseInt(e.target.value) || 0 })}
                  className="w-full px-3 py-2 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-pink-500"
                />
              </div>
              <div className="flex space-x-3">
                <button onClick={handleSaveCategory}
                  className="flex-1 px-4 py-2 bg-pink-600 text-white rounded-xl hover:bg-pink-700">
                  保存
                </button>
                <button onClick={() => setShowCategoryModal(false)}
                  className="px-4 py-2 border border-slate-200 rounded-xl hover:bg-slate-50">
                  取消
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );

  const renderPosts = () => (
    <div className="space-y-6">
      <div className="bg-white rounded-2xl p-6 shadow-sm border border-slate-100">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl font-bold text-slate-900">帖子管理</h2>
          <div className="flex items-center space-x-4">
            <div className="relative">
              <input
                type="text"
                placeholder="搜索帖子..."
                value={postSearch}
                onChange={(e) => setPostSearch(e.target.value)}
                onKeyPress={(e) => {
                  if (e.key === 'Enter') {
                    setPostPage(1);
                    loadPosts();
                  }
                }}
                className="pl-10 pr-4 py-2 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-pink-500"
              />
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-400" />
            </div>
            <button onClick={() => { setPostPage(1); loadPosts(); }} className="px-4 py-2 bg-pink-600 text-white rounded-xl hover:bg-pink-700">
              搜索
            </button>
            <button onClick={() => { setPostSearch(''); setPostPage(1); loadPosts(); }} className="px-4 py-2 border border-slate-200 rounded-xl hover:bg-slate-100">
              重置
            </button>
            <button onClick={loadPosts} className="p-2 hover:bg-slate-100 rounded-xl transition-colors">
              <RefreshCw className="w-5 h-5 text-slate-600" />
            </button>
          </div>
        </div>

        <div className="space-y-4">
          {posts.map((post) => (
            <div key={post.id} className="border border-slate-200 rounded-xl p-4 hover:shadow-md transition-shadow">
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <h3 className="font-semibold text-slate-900">
                    <a href={`/post/${post.id}`} target="_blank" rel="noopener noreferrer" className="hover:text-pink-600 transition-colors">
                      {post.title}
                    </a>
                  </h3>
                  <div className="flex items-center space-x-4 mt-2 text-sm text-slate-500">
                    <span>作者: {post.author_name}</span>
                    <span>版块: {post.category_name}</span>
                    <span className="flex items-center">
                      <Eye className="w-4 h-4 mr-1" />
                      {post.views}
                    </span>
                    <span className="flex items-center">
                      <MessageSquare className="w-4 h-4 mr-1" />
                      {post.replies_count}
                    </span>
                  </div>
                  <p className="text-xs text-slate-400 mt-2">
                    {new Date(post.created_at).toLocaleString()}
                  </p>
                </div>
                <div className="flex items-center space-x-2">
                  <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                    post.status === 'normal' ? 'bg-green-100 text-green-700' :
                    post.status === 'hidden' ? 'bg-yellow-100 text-yellow-700' :
                    'bg-red-100 text-red-700'
                  }`}>
                    {post.status === 'normal' ? '正常' : post.status === 'hidden' ? '隐藏' : '删除'}
                  </span>
                  <button
                    onClick={() => handleSetTop(post.id, post.is_top, 'global')}
                    className={`px-3 py-1.5 text-xs rounded-lg transition-colors ${
                      post.is_top === 'global' ? 'bg-red-100 text-red-600 font-medium' : 'hover:bg-pink-50 text-pink-600'
                    }`}
                    title={post.is_top === 'global' ? '取消全站置顶' : '全站置顶'}
                  >
                    全站置顶
                  </button>
                  <button
                    onClick={() => handleSetTop(post.id, post.is_top, 'category')}
                    className={`px-3 py-1.5 text-xs rounded-lg transition-colors ${
                      post.is_top === 'category' ? 'bg-pink-100 text-pink-600 font-medium' : 'hover:bg-pink-50 text-pink-600'
                    }`}
                    title={post.is_top === 'category' ? '取消板块置顶' : '板块置顶'}
                  >
                    板块置顶
                  </button>
                  <button
                    onClick={() => handleToggleEssence(post.id, post.is_essence)}
                    className={`p-2 rounded-lg transition-colors ${
                      post.is_essence ? 'bg-yellow-50 text-yellow-600' : 'hover:bg-yellow-50 text-yellow-600'
                    }`}
                    title={post.is_essence ? '取消精华' : '设为精华'}
                  >
                    <Star className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => handleMovePost(post)}
                    className="px-3 py-1.5 text-xs rounded-lg hover:bg-blue-50 text-blue-600 transition-colors"
                    title="移动帖子到其他版块"
                  >
                    移动
                  </button>
                  <button onClick={() => handleDeletePost(post.id)} className="p-2 hover:bg-red-50 rounded-lg">
                    <Trash2 className="w-4 h-4 text-red-600" />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>

        <div className="mt-4 flex items-center justify-between">
          <p className="text-sm text-slate-500">共 {postTotal} 篇帖子</p>
          <div className="flex items-center space-x-2">
            <button onClick={() => setPostPage(p => Math.max(1, p - 1))} disabled={postPage === 1}
              className="px-3 py-1 border border-slate-200 rounded-lg disabled:opacity-50">
              上一页
            </button>
            <span className="text-sm text-slate-600">第 {postPage} 页</span>
            <button onClick={() => setPostPage(p => p + 1)} disabled={posts.length < 20}
              className="px-3 py-1 border border-slate-200 rounded-lg disabled:opacity-50">
              下一页
            </button>
          </div>
        </div>
      </div>
    </div>
  );

  const renderSensitiveWords = () => (
    <div className="space-y-6">
      <div className="bg-white rounded-2xl p-6 shadow-sm border border-slate-100">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl font-bold text-slate-900">敏感词库</h2>
          <div className="flex items-center space-x-3">
            <button
              onClick={() => setShowBatchModal(true)}
              className="flex items-center space-x-2 px-4 py-2 bg-green-600 text-white rounded-xl hover:bg-green-700"
            >
              <FileText className="w-4 h-4" />
              <span>批量导入</span>
            </button>
            <button onClick={loadSensitiveWords} className="p-2 hover:bg-slate-100 rounded-xl transition-colors">
              <RefreshCw className="w-5 h-5 text-slate-600" />
            </button>
          </div>
        </div>

        <div className="space-y-4">
          <div className="flex space-x-3">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-slate-400" />
              <input
                type="text"
                value={searchWord}
                onChange={(e) => setSearchWord(e.target.value)}
                placeholder="搜索敏感词..."
                className="w-full pl-10 pr-4 py-2 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-pink-500"
              />
            </div>
          </div>
          <div className="flex space-x-3">
            <input
              type="text"
              value={newWord}
              onChange={(e) => setNewWord(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && handleAddSensitiveWord()}
              placeholder="输入敏感词..."
              className="flex-1 px-4 py-2 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-pink-500"
            />
            <button onClick={handleAddSensitiveWord}
              className="px-6 py-2 bg-pink-600 text-white rounded-xl hover:bg-pink-700">
              添加
            </button>
          </div>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3">
          {sensitiveWords.map((word) => (
            <div key={word.id}
              className="flex items-center justify-between px-3 py-2 bg-slate-50 rounded-lg hover:bg-slate-100 transition-colors">
              <span className="text-sm text-slate-700">{word.word}</span>
              <button onClick={() => handleDeleteSensitiveWord(word.id)} className="text-slate-400 hover:text-red-600">
                <X className="w-4 h-4" />
              </button>
            </div>
          ))}
        </div>

        {sensitiveWords.length === 0 && (
          <p className="text-center text-slate-500 py-8">暂无敏感词</p>
        )}
      </div>

      {showBatchModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-2xl p-6 w-full max-w-2xl">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-bold text-slate-900">批量导入敏感词</h3>
              <button onClick={() => setShowBatchModal(false)}>
                <X className="w-5 h-5 text-slate-500" />
              </button>
            </div>
            <p className="text-sm text-slate-500 mb-4">每行一个敏感词，导入后将立即生效</p>
            <textarea
              value={batchWords}
              onChange={(e) => setBatchWords(e.target.value)}
              rows={10}
              placeholder="敏感词1&#10;敏感词2&#10;敏感词3&#10;..."
              className="w-full px-3 py-2 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-pink-500 font-mono"
            />
            <div className="flex space-x-3 mt-4">
              <button onClick={handleBatchImport}
                className="flex-1 px-4 py-2 bg-pink-600 text-white rounded-xl hover:bg-pink-700">
                导入
              </button>
              <button onClick={() => setShowBatchModal(false)}
                className="px-4 py-2 border border-slate-200 rounded-xl hover:bg-slate-50">
                取消
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );

  const renderSiteConfig = () => (
    <div className="space-y-6">
      <div className="bg-white rounded-2xl p-6 shadow-sm border border-slate-100">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl font-bold text-slate-900">站点配置</h2>
          <button onClick={loadConfigs} className="p-2 hover:bg-slate-100 rounded-xl transition-colors">
            <RefreshCw className="w-5 h-5 text-slate-600" />
          </button>
        </div>

        {Object.entries(configs).map(([groupName, groupConfigs]) => (
          <div key={groupName} className="mb-8">
            <h3 className="text-lg font-semibold text-slate-800 mb-4">
              {groupName === 'basic' ? '基本设置' : groupName === 'content' ? '内容设置' : groupName}
            </h3>
            <div className="space-y-3">
              {groupConfigs.map((config) => (
                <div key={config.id} className="flex items-center justify-between p-4 bg-slate-50 rounded-xl">
                  <div className="flex-1">
                    <div className="font-medium text-slate-900">{config.description || config.config_key}</div>
                    <div className="text-xs text-slate-500 mt-1">键: {config.config_key}</div>
                  </div>
                  <div className="flex items-center space-x-3">
                    {editingConfig?.id === config.id ? (
                      <>
                        <input
                          type="text"
                          value={editValue}
                          onChange={(e) => setEditValue(e.target.value)}
                          className="px-3 py-1 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-pink-500"
                        />
                        <button onClick={handleSaveConfig} className="p-2 bg-green-600 text-white rounded-lg hover:bg-green-700">
                          <Save className="w-4 h-4" />
                        </button>
                        <button onClick={() => setEditingConfig(null)} className="p-2 bg-slate-200 text-slate-600 rounded-lg hover:bg-slate-300">
                          <X className="w-4 h-4" />
                        </button>
                      </>
                    ) : (
                      <>
                        <span className="text-sm text-slate-600 max-w-xs truncate">
                          {config.config_value || '(空)'}
                        </span>
                        {config.is_editable && (
                          <button onClick={() => {
                            setEditingConfig(config);
                            setEditValue(config.config_value || '');
                          }} className="p-2 hover:bg-pink-100 rounded-lg">
                            <Edit2 className="w-4 h-4 text-pink-600" />
                          </button>
                        )}
                      </>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        ))}

        {/* 添加Telegram账号配置项 */}
        <div className="mb-8">
          <h3 className="text-lg font-semibold text-slate-800 mb-4">联系方式设置</h3>
          <div className="space-y-3">
            <div className="flex items-center justify-between p-4 bg-slate-50 rounded-xl">
              <div className="flex-1">
                <div className="font-medium text-slate-900">管理员Telegram账号</div>
                <div className="text-xs text-slate-500 mt-1">键: admin_telegram</div>
              </div>
              <div className="flex items-center space-x-3">
                {editingConfig?.config_key === 'admin_telegram' ? (
                  <>
                    <input
                      type="text"
                      value={editValue}
                      onChange={(e) => setEditValue(e.target.value)}
                      className="px-3 py-1 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-pink-500"
                    />
                    <button onClick={handleSaveConfig} className="p-2 bg-green-600 text-white rounded-lg hover:bg-green-700">
                      <Save className="w-4 h-4" />
                    </button>
                    <button onClick={() => setEditingConfig(null)} className="p-2 bg-slate-200 text-slate-600 rounded-lg hover:bg-slate-300">
                      <X className="w-4 h-4" />
                    </button>
                  </>
                ) : (
                  <>
                    <span className="text-sm text-slate-600 max-w-xs truncate">
                      {configs?.basic?.find(c => c.config_key === 'admin_telegram')?.config_value || '(空)'}
                    </span>
                    <button onClick={() => {
                      setEditingConfig({ id: 'admin_telegram', config_key: 'admin_telegram', config_value: configs?.basic?.find(c => c.config_key === 'admin_telegram')?.config_value || '', is_editable: true });
                      setEditValue(configs?.basic?.find(c => c.config_key === 'admin_telegram')?.config_value || '');
                    }} className="p-2 hover:bg-pink-100 rounded-lg">
                      <Edit2 className="w-4 h-4 text-pink-600" />
                    </button>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* 添加音乐播放器配置项 */}
        <div className="mb-8">
          <h3 className="text-lg font-semibold text-slate-800 mb-4">音乐播放器设置</h3>
          <div className="space-y-3">
            <div className="flex items-center justify-between p-4 bg-slate-50 rounded-xl">
              <div className="flex-1">
                <div className="font-medium text-slate-900">网易云音乐歌曲ID</div>
                <div className="text-xs text-slate-500 mt-1">键: music_song_id</div>
              </div>
              <div className="flex items-center space-x-3">
                {editingConfig?.config_key === 'music_song_id' ? (
                  <>
                    <input
                      type="text"
                      value={editValue}
                      onChange={(e) => setEditValue(e.target.value)}
                      className="px-3 py-1 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-pink-500"
                    />
                    <button onClick={handleSaveConfig} className="p-2 bg-green-600 text-white rounded-lg hover:bg-green-700">
                      <Save className="w-4 h-4" />
                    </button>
                    <button onClick={() => setEditingConfig(null)} className="p-2 bg-slate-200 text-slate-600 rounded-lg hover:bg-slate-300">
                      <X className="w-4 h-4" />
                    </button>
                  </>
                ) : (
                  <>
                    <span className="text-sm text-slate-600 max-w-xs truncate">
                      {configs?.basic?.find(c => c.config_key === 'music_song_id')?.config_value || '(空)'}
                    </span>
                    <button onClick={() => {
                      setEditingConfig({ id: 'music_song_id', config_key: 'music_song_id', config_value: configs?.basic?.find(c => c.config_key === 'music_song_id')?.config_value || '', is_editable: true });
                      setEditValue(configs?.basic?.find(c => c.config_key === 'music_song_id')?.config_value || '');
                    }} className="p-2 hover:bg-pink-100 rounded-lg">
                      <Edit2 className="w-4 h-4 text-pink-600" />
                    </button>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  const renderAds = () => (
    <div className="space-y-6">
      <div className="bg-white rounded-2xl p-6 shadow-sm border border-slate-100">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl font-bold text-slate-900">广告管理</h2>
          <button onClick={loadAdSpots} className="p-2 hover:bg-slate-100 rounded-xl transition-colors">
            <RefreshCw className="w-5 h-5 text-slate-600" />
          </button>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
          <div className="lg:col-span-1">
            <h3 className="text-sm font-semibold text-slate-700 mb-3">广告位</h3>
            <div 
              className="space-y-2"
              onDragOver={(e) => e.preventDefault()}
              onDrop={(e) => handleDrop(e)}
            >
              {adSpots.map((spot, index) => (
                <div
                  key={spot.id}
                  draggable
                  onDragStart={(e) => handleDragStart(e, spot.id!)}
                  onClick={() => {
                    setSelectedSpot(spot.id!);
                    loadAdsForSpot(spot.id!);
                  }}
                  className={`w-full text-left px-4 py-3 rounded-xl transition-colors cursor-move ${selectedSpot === spot.id ? 'bg-pink-600 text-white' : 'bg-slate-50 hover:bg-slate-100'}`}
                >
                  <div className="font-medium flex items-center justify-between">
                    <span>{spot.spot_name}</span>
                    <span className="text-xs bg-slate-200 text-slate-700 px-2 py-0.5 rounded-full">
                      {index + 1}
                    </span>
                  </div>
                  <div className={`text-xs mt-1 ${selectedSpot === spot.id ? 'text-pink-100' : 'text-slate-500'}`}>
                    {spot.position}
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="lg:col-span-3">
            {selectedSpot ? (
              <>
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-sm font-semibold text-slate-700">广告列表</h3>
                  <button
                    onClick={() => {
                      setEditingAd(null);
                      setAdForm({
                        title: '',
                        content: '',
                        image_url: '',
                        link_url: '',
                        start_time: '',
                        end_time: '',
                        is_active: true,
                        sort_order: 0
                      });
                      setShowAdModal(true);
                    }}
                    className="flex items-center space-x-2 px-4 py-2 bg-pink-600 text-white rounded-xl hover:bg-pink-700"
                  >
                    <Plus className="w-4 h-4" />
                    <span>添加广告</span>
                  </button>
                </div>

                <div 
                  className="space-y-4"
                  onDragOver={(e) => e.preventDefault()}
                  onDrop={(e) => handleAdDrop(e)}
                >
                  {ads.map((ad, index) => (
                    <div 
                      key={ad.id} 
                      data-ad-id={ad.id}
                      className="border border-slate-200 rounded-xl p-4 cursor-move"
                      draggable
                      onDragStart={(e) => handleAdDragStart(e, ad.id!)}
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center">
                            <span className="text-xs bg-slate-200 text-slate-700 px-2 py-0.5 rounded-full mr-3">
                              {index + 1}
                            </span>
                            <h4 className="font-medium text-slate-900">{ad.title || '无标题'}</h4>
                          </div>
                          <p className="text-sm text-slate-500 mt-1">{ad.content || '无内容'}</p>
                          <div className="flex items-center space-x-4 mt-2 text-xs text-slate-400">
                            <span>点击: {ad.click_count}</span>
                            <span>状态: {ad.is_active ? '启用' : '禁用'}</span>
                          </div>
                        </div>
                        <div className="flex items-center space-x-2">
                          <button onClick={() => {
                            setEditingAd(ad);
                            setAdForm({
                              title: ad.title || '',
                              content: ad.content || '',
                              image_url: ad.image_url || '',
                              link_url: ad.link_url || '',
                              ad_type: ad.ad_type || 'image',
                              start_time: ad.start_time || '',
                              end_time: ad.end_time || '',
                              is_active: ad.is_active,
                              sort_order: ad.sort_order || 0
                            });
                            setShowAdModal(true);
                          }} className="p-2 hover:bg-slate-100 rounded-lg">
                            <Edit2 className="w-4 h-4 text-slate-600" />
                          </button>
                          <button onClick={() => handleDeleteAd(ad.id!)} className="p-2 hover:bg-red-50 rounded-lg">
                            <Trash2 className="w-4 h-4 text-red-600" />
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}

                  {ads.length === 0 && (
                    <p className="text-center text-slate-500 py-8">该广告位暂无广告</p>
                  )}
                </div>
              </>
            ) : (
              <div className="flex items-center justify-center h-64 text-slate-500">
                请选择一个广告位
              </div>
            )}
          </div>
        </div>
      </div>

      {showAdModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-2xl p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-bold text-slate-900">
                {editingAd ? '编辑广告' : '添加广告'}
              </h3>
              <button onClick={() => setShowAdModal(false)}>
                <X className="w-5 h-5 text-slate-500" />
              </button>
            </div>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">标题</label>
                <input type="text" value={adForm.title}
                  onChange={(e) => setAdForm({ ...adForm, title: e.target.value })}
                  className="w-full px-3 py-2 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-pink-500" />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">广告类型</label>
                <div className="flex space-x-4">
                  <label className="flex items-center space-x-2 cursor-pointer">
                    <input type="radio" name="ad_type" value="image" checked={adForm.ad_type === 'image'}
                      onChange={(e) => setAdForm({ ...adForm, ad_type: 'image' })}
                      className="w-4 h-4 text-pink-600" />
                    <span className="text-sm text-slate-700">图片广告</span>
                  </label>
                  <label className="flex items-center space-x-2 cursor-pointer">
                    <input type="radio" name="ad_type" value="code" checked={adForm.ad_type === 'code'}
                      onChange={(e) => setAdForm({ ...adForm, ad_type: 'code' })}
                      className="w-4 h-4 text-pink-600" />
                    <span className="text-sm text-slate-700">代码广告 (如 Adsterra)</span>
                  </label>
                </div>
              </div>
              {adForm.ad_type === 'code' ? (
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">广告代码</label>
                  <textarea value={adForm.content}
                    onChange={(e) => setAdForm({ ...adForm, content: e.target.value })}
                    rows={8}
                    placeholder="请粘贴 Adsterra 或其他广告平台的代码..."
                    className="w-full px-3 py-2 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-pink-500 font-mono text-xs" />
                </div>
              ) : (
                <>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">内容</label>
                    <textarea value={adForm.content}
                      onChange={(e) => setAdForm({ ...adForm, content: e.target.value })}
                      rows={3}
                      className="w-full px-3 py-2 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-pink-500" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">图片URL</label>
                    <input type="text" value={adForm.image_url}
                      onChange={(e) => setAdForm({ ...adForm, image_url: e.target.value })}
                      className="w-full px-3 py-2 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-pink-500" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">链接URL</label>
                    <input type="text" value={adForm.link_url}
                      onChange={(e) => setAdForm({ ...adForm, link_url: e.target.value })}
                      className="w-full px-3 py-2 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-pink-500" />
                  </div>
                </>
              )}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">排序值</label>
                <input type="number" value={adForm.sort_order}
                  onChange={(e) => setAdForm({ ...adForm, sort_order: parseInt(e.target.value) || 0 })}
                  min="0"
                  className="w-full px-3 py-2 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-pink-500" />
                <p className="text-xs text-slate-500 mt-1">数字越小，排序越靠前</p>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">开始时间</label>
                  <input type="datetime-local" value={adForm.start_time}
                    onChange={(e) => setAdForm({ ...adForm, start_time: e.target.value })}
                    className="w-full px-3 py-2 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-pink-500" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">结束时间</label>
                  <input type="datetime-local" value={adForm.end_time}
                    onChange={(e) => setAdForm({ ...adForm, end_time: e.target.value })}
                    className="w-full px-3 py-2 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-pink-500" />
                </div>
              </div>
              <div className="flex items-center space-x-2">
                <input type="checkbox" id="is_active" checked={adForm.is_active}
                  onChange={(e) => setAdForm({ ...adForm, is_active: e.target.checked })}
                  className="w-4 h-4" />
                <label htmlFor="is_active" className="text-sm text-slate-700">启用广告</label>
              </div>
              <div className="flex space-x-3">
                <button onClick={handleSaveAd}
                  className="flex-1 px-4 py-2 bg-pink-600 text-white rounded-xl hover:bg-pink-700">
                  保存
                </button>
                <button onClick={() => setShowAdModal(false)}
                  className="px-4 py-2 border border-slate-200 rounded-xl hover:bg-slate-50">
                  取消
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );

  const renderStats = () => (
    <div className="space-y-6">
      <div className="bg-white rounded-2xl p-6 shadow-sm border border-slate-100">
        <h2 className="text-xl font-bold text-slate-900 mb-6">数据统计</h2>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <div className="bg-gradient-to-br from-pink-500 to-pink-600 rounded-xl p-6 text-white">
            <div className="text-3xl font-bold mb-2">{stats.totalUsers}</div>
            <div className="text-pink-100">总用户数</div>
          </div>
          <div className="bg-gradient-to-br from-green-500 to-green-600 rounded-xl p-6 text-white">
            <div className="text-3xl font-bold mb-2">{stats.totalPosts}</div>
            <div className="text-green-100">总帖子数</div>
          </div>
          <div className="bg-gradient-to-br from-purple-500 to-purple-600 rounded-xl p-6 text-white">
            <div className="text-3xl font-bold mb-2">{stats.totalReplies}</div>
            <div className="text-purple-100">总回复数</div>
          </div>
          <div className="bg-gradient-to-br from-orange-500 to-orange-600 rounded-xl p-6 text-white">
            <div className="text-3xl font-bold mb-2">{countAllCategories(categories)}</div>
            <div className="text-orange-100">总版块数</div>
          </div>
        </div>

        <div className="border-t border-slate-200 pt-6">
          <h3 className="text-lg font-semibold text-slate-800 mb-4">快捷统计</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="p-4 bg-slate-50 rounded-xl">
              <div className="text-2xl font-bold text-slate-900">{allSensitiveWords.length}</div>
              <div className="text-sm text-slate-500">敏感词数量</div>
            </div>
            <div className="p-4 bg-slate-50 rounded-xl">
              <div className="text-2xl font-bold text-slate-900">{adSpots.length}</div>
              <div className="text-sm text-slate-500">广告位数</div>
            </div>
            <div className="p-4 bg-slate-50 rounded-xl">
              <div className="text-2xl font-bold text-slate-900">
                {Object.keys(configs).reduce((sum, key) => sum + (configs[key]?.length || 0), 0)}
              </div>
              <div className="text-sm text-slate-500">配置项总数</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  const renderOperationLogs = () => {
    // 动态导入操作日志组件
    const AdminOperationLogs = React.lazy(() => import('./AdminOperationLogs'));
    return (
      <React.Suspense fallback={
        <div className="flex items-center justify-center h-64">
          <RefreshCw className="w-8 h-8 animate-spin text-pink-600" />
        </div>
      }>
        <AdminOperationLogs />
      </React.Suspense>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-pink-600 mx-auto mb-4"></div>
          <p className="text-slate-600">加载中...</p>
        </div>
      </div>
    );
  }

  if (!isAuthenticated || !['管理员', '站长', '后台设置成员'].includes(user?.role || '')) {
    return <Navigate to="/" replace />;
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100">
      <header className="bg-white shadow-sm border-b border-slate-200">
        <div className="flex items-center justify-between px-4 sm:px-6 py-3 sm:py-4">
          <div className="flex items-center space-x-3 sm:space-x-4">
            {isMobile && (
              <button
                onClick={() => setIsMobileSidebarOpen(true)}
                className="p-2 hover:bg-slate-100 rounded-lg transition-colors"
              >
                <Menu className="w-5 h-5 text-slate-600" />
              </button>
            )}
            <div className="w-8 h-8 sm:w-10 sm:h-10 bg-gradient-to-br from-pink-600 to-pink-700 rounded-xl flex items-center justify-center shadow-lg">
              <span className="text-white font-bold text-sm sm:text-xl">桃</span>
            </div>
            <div className="flex items-center space-x-2">
              <h1 className="text-sm sm:text-xl font-bold text-slate-900">站长后台</h1>
              <button
                onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
                className="p-2 hover:bg-slate-100 rounded-lg transition-colors hidden lg:block"
                title={sidebarCollapsed ? '展开菜单' : '收起菜单'}
              >
                <Menu className="w-5 h-5 text-slate-600" />
              </button>
            </div>
            <p className="text-xs text-slate-500 hidden sm:block">桃花坛</p>
          </div>

          <div className="flex items-center space-x-2 sm:space-x-4">
            <button
              onClick={() => navigate('/')}
              className="flex items-center space-x-1 sm:space-x-2 px-2 sm:px-4 py-1.5 sm:py-2 text-sm text-slate-600 hover:text-slate-900 hover:bg-slate-100 rounded-lg transition-colors"
            >
              <Eye className="w-4 h-4" />
              <span className="hidden sm:inline">返回前台</span>
            </button>
            <div className="flex items-center space-x-2 px-2 sm:px-4 py-1.5 sm:py-2 bg-slate-100 rounded-lg">
              <img
                src={user?.avatar || `https://ui-avatars.com/api/?name=${user?.nickname || user?.username || 'User'}&background=3b82f6&color=fff`}
                alt={user?.nickname}
                className="w-6 h-6 sm:w-8 sm:h-8 rounded-full"
              />
              <div className="hidden sm:block">
                <p className="text-sm font-medium text-slate-900">{user?.nickname}</p>
                <p className="text-xs text-slate-500">{user?.role}</p>
              </div>
            </div>
            <button onClick={handleLogout}
              className="flex items-center space-x-1 sm:space-x-2 px-2 sm:px-4 py-1.5 sm:py-2 text-sm text-slate-600 hover:text-slate-900 hover:bg-slate-100 rounded-lg transition-colors">
              <LogOut className="w-4 h-4" />
              <span className="hidden sm:inline">退出</span>
            </button>
          </div>
        </div>
      </header>

      <div className="flex">
        {isMobileSidebarOpen && (
          <div 
            className="fixed inset-0 bg-black/50 z-40 lg:hidden"
            onClick={() => setIsMobileSidebarOpen(false)}
          />
        )}
        <aside className={`${sidebarCollapsed && !isMobileSidebarOpen ? 'w-20' : 'w-64'} bg-white border-r border-slate-200 min-h-screen transition-all duration-300 relative z-50 lg:z-0 ${
          isMobileSidebarOpen ? 'fixed inset-y-0 left-0' : ''
        }`}>
          <div className="flex items-center justify-between p-4 border-b border-slate-200 lg:hidden">
            <span className="font-bold text-slate-900">导航菜单</span>
            <button
              onClick={() => setIsMobileSidebarOpen(false)}
              className="p-2 hover:bg-slate-100 rounded-lg transition-colors"
            >
              <X className="w-5 h-5 text-slate-600" />
            </button>
          </div>
          
          <nav className="p-4 space-y-1">
            {menuItems.map((item) => {
              const Icon = item.icon;
              const isActive = activeMenu === item.id;
              const hasChildren = item.children && item.children.length > 0;
              const isExpanded = expandedMenu === item.id;

              if (hasChildren) {
                return (
                  <div key={item.id}>
                    <button
                      onClick={() => {
                        setExpandedMenu(isExpanded ? null : item.id);
                        setActiveMenu(item.id);
                      }}
                      className={`w-full flex items-center justify-between px-4 py-3 rounded-xl transition-all ${
                        isActive
                          ? 'bg-gradient-to-r from-pink-600 to-pink-700 text-white shadow-lg shadow-pink-600/30'
                          : 'text-slate-600 hover:bg-slate-100'
                      }`}
                    >
                      <div className="flex items-center space-x-3">
                        <Icon className="w-5 h-5" />
                        {(!sidebarCollapsed || isMobileSidebarOpen) && <span className="font-medium">{item.label}</span>}
                      </div>
                      {(!sidebarCollapsed || isMobileSidebarOpen) && (
                        <ChevronRight className={`w-4 h-4 transition-transform ${isExpanded ? 'rotate-90' : ''}`} />
                      )}
                    </button>
                    {(isMobileSidebarOpen || !sidebarCollapsed) && isExpanded && item.children && (
                      <div className="ml-4 mt-1 space-y-1">
                        {item.children.map((child) => {
                          const ChildIcon = child.icon;
                          const isChildActive = activeMenu === child.id;
                          return (
                            <button
                              key={child.id}
                              onClick={() => {
                                setActiveMenu(child.id);
                                if (isMobile) {
                                  setIsMobileSidebarOpen(false);
                                }
                              }}
                              className={`w-full flex items-center space-x-3 px-4 py-2 rounded-lg transition-all text-sm ${
                                isChildActive
                                  ? 'bg-pink-100 text-pink-700 font-medium'
                                  : 'text-slate-500 hover:bg-slate-50'
                              }`}
                            >
                              <ChildIcon className="w-4 h-4" />
                              <span>{child.label}</span>
                            </button>
                          );
                        })}
                      </div>
                    )}
                  </div>
                );
              }

              return (
                <button
                  key={item.id}
                  onClick={() => {
                    setActiveMenu(item.id);
                    if (isMobile) {
                      setIsMobileSidebarOpen(false);
                    }
                  }}
                  className={`w-full flex items-center space-x-3 px-4 py-3 rounded-xl transition-all ${
                    isActive
                      ? 'bg-gradient-to-r from-pink-600 to-pink-700 text-white shadow-lg shadow-pink-600/30'
                      : 'text-slate-600 hover:bg-slate-100'
                  }`}
                >
                  <Icon className="w-5 h-5" />
                  {(!sidebarCollapsed || isMobileSidebarOpen) && <span className="font-medium">{item.label}</span>}
                </button>
              );
            })}
          </nav>
        </aside>

        <main className="flex-1 p-4 sm:p-6 lg:p-8 overflow-y-auto" style={{ maxHeight: 'calc(100vh - 80px)' }}>
          {renderContent()}
          
          {/* 删除确认对话框 */}
          {userToDelete && (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
              <div className="bg-white rounded-2xl p-6 w-full max-w-md shadow-lg">
                <h3 className="text-lg font-semibold text-slate-900 mb-4">确认删除</h3>
                <p className="text-slate-600 mb-6">确定要删除该用户吗？此操作不可恢复！</p>
                <div className="flex items-center justify-end space-x-4">
                  <button
                    onClick={cancelDeleteUser}
                    className="px-4 py-2 border border-slate-200 rounded-xl hover:bg-slate-100"
                  >
                    取消
                  </button>
                  <button
                    onClick={confirmDeleteUser}
                    className="px-4 py-2 bg-red-600 text-white rounded-xl hover:bg-red-700"
                  >
                    确认删除
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* 用户组删除确认对话框 */}
          {userGroupToDelete && (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
              <div className="bg-white rounded-2xl p-6 w-full max-w-md shadow-lg">
                <h3 className="text-lg font-semibold text-slate-900 mb-4">确认删除</h3>
                <p className="text-slate-600 mb-6">确定要删除这个用户组吗？此操作不可恢复！</p>
                <div className="flex items-center justify-end space-x-4">
                  <button
                    onClick={cancelDeleteUserGroup}
                    className="px-4 py-2 border border-slate-200 rounded-xl hover:bg-slate-100"
                  >
                    取消
                  </button>
                  <button
                    onClick={confirmDeleteUserGroup}
                    className="px-4 py-2 bg-red-600 text-white rounded-xl hover:bg-red-700"
                  >
                    确认删除
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* 版块删除确认对话框 */}
          {categoryToDelete && (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
              <div className="bg-white rounded-2xl p-6 w-full max-w-md shadow-lg">
                <h3 className="text-lg font-semibold text-slate-900 mb-4">确认删除</h3>
                <p className="text-slate-600 mb-6">确定要删除这个版块吗？此操作不可恢复！</p>
                <div className="flex items-center justify-end space-x-4">
                  <button
                    onClick={cancelDeleteCategory}
                    className="px-4 py-2 border border-slate-200 rounded-xl hover:bg-slate-100"
                  >
                    取消
                  </button>
                  <button
                    onClick={confirmDeleteCategory}
                    className="px-4 py-2 bg-red-600 text-white rounded-xl hover:bg-red-700"
                  >
                    确认删除
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* 帖子删除确认对话框 */}
          {postToDelete && (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
              <div className="bg-white rounded-2xl p-6 w-full max-w-md shadow-lg">
                <h3 className="text-lg font-semibold text-slate-900 mb-4">确认删除</h3>
                <p className="text-slate-600 mb-6">确定要删除这篇帖子吗？此操作不可恢复！</p>
                <div className="flex items-center justify-end space-x-4">
                  <button
                    onClick={cancelDeletePost}
                    className="px-4 py-2 border border-slate-200 rounded-xl hover:bg-slate-100"
                  >
                    取消
                  </button>
                  <button
                    onClick={confirmDeletePost}
                    className="px-4 py-2 bg-red-600 text-white rounded-xl hover:bg-red-700"
                  >
                    确认删除
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* 移动帖子对话框 */}
          {showMovePostModal && postToMove && (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
              <div className="bg-white rounded-2xl p-6 w-full max-w-md shadow-lg max-h-[80vh] overflow-y-auto">
                <h3 className="text-lg font-semibold text-slate-900 mb-4">移动帖子</h3>
                <p className="text-sm text-slate-600 mb-2">帖子：{postToMove.title}</p>
                <p className="text-sm text-slate-600 mb-4">当前版块：{postToMove.category_name}</p>
                <div className="mb-4">
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    选择目标版块
                  </label>
                  <select
                    value={selectedPostCategory || ''}
                    onChange={(e) => setSelectedPostCategory(parseInt(e.target.value) || null)}
                    className="w-full px-3 py-2 border border-slate-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-pink-500"
                  >
                    <option value="">请选择版块</option>
                    {categories.map((cat) => (
                      <React.Fragment key={cat.id}>
                        <option value={cat.id}>{cat.name}</option>
                        {cat.children && cat.children.length > 0 && cat.children.map((child: any) => (
                          <option key={child.id} value={child.id}>
                            {'　└ '}{child.name}
                          </option>
                        ))}
                      </React.Fragment>
                    ))}
                  </select>
                </div>
                <div className="flex items-center justify-end space-x-4">
                  <button
                    onClick={() => {
                      setShowMovePostModal(false);
                      setPostToMove(null);
                    }}
                    className="px-4 py-2 border border-slate-200 rounded-xl hover:bg-slate-100"
                  >
                    取消
                  </button>
                  <button
                    onClick={confirmMovePost}
                    disabled={!selectedPostCategory || selectedPostCategory === postToMove.category_id}
                    className="px-4 py-2 bg-blue-600 text-white rounded-xl hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    确认移动
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* 敏感词删除确认对话框 */}
          {sensitiveWordToDelete && (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
              <div className="bg-white rounded-2xl p-6 w-full max-w-md shadow-lg">
                <h3 className="text-lg font-semibold text-slate-900 mb-4">确认删除</h3>
                <p className="text-slate-600 mb-6">确定要删除该敏感词吗？此操作不可恢复！</p>
                <div className="flex items-center justify-end space-x-4">
                  <button
                    onClick={cancelDeleteSensitiveWord}
                    className="px-4 py-2 border border-slate-200 rounded-xl hover:bg-slate-100"
                  >
                    取消
                  </button>
                  <button
                    onClick={confirmDeleteSensitiveWord}
                    className="px-4 py-2 bg-red-600 text-white rounded-xl hover:bg-red-700"
                  >
                    确认删除
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* 广告删除确认对话框 */}
          {adToDelete && (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
              <div className="bg-white rounded-2xl p-6 w-full max-w-md shadow-lg">
                <h3 className="text-lg font-semibold text-slate-900 mb-4">确认删除</h3>
                <p className="text-slate-600 mb-6">确定要删除这个广告吗？此操作不可恢复！</p>
                <div className="flex items-center justify-end space-x-4">
                  <button
                    onClick={cancelDeleteAd}
                    className="px-4 py-2 border border-slate-200 rounded-xl hover:bg-slate-100"
                  >
                    取消
                  </button>
                  <button
                    onClick={confirmDeleteAd}
                    className="px-4 py-2 bg-red-600 text-white rounded-xl hover:bg-red-700"
                  >
                    确认删除
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* 操作确认对话框 */}
          {confirmAction && (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
              <div className="bg-white rounded-2xl p-6 w-full max-w-md shadow-lg">
                <h3 className="text-lg font-semibold text-slate-900 mb-4">确认操作</h3>
                <p className="text-slate-600 mb-6">
                  {confirmAction.type === 'ban' && '确定要禁用该用户吗？'}
                  {confirmAction.type === 'unban' && '确定要启用该用户吗？'}
                  {confirmAction.type === 'sendVerification' && '确定要发送验证邮件吗？'}
                  {confirmAction.type === 'sendResetPassword' && '确定要发送重置密码邮件吗？'}
                </p>
                <div className="flex items-center justify-end space-x-4">
                  <button
                    onClick={cancelAction}
                    className="px-4 py-2 border border-slate-200 rounded-xl hover:bg-slate-100"
                  >
                    取消
                  </button>
                  <button
                    onClick={confirmActionHandler}
                    className="px-4 py-2 bg-pink-600 text-white rounded-xl hover:bg-pink-700"
                  >
                    确认
                  </button>
                </div>
              </div>
            </div>
          )}
        </main>
      </div>
    </div>
  );
};

export default AdminMaster;
